<template>
  <div class="entity-list-page ab-testing-dashboard">
    <header class="list-header">
      <div>
        <router-link to="/projects" class="back-link">← Projects</router-link>
        <h1>A/B testing</h1>
        <p class="list-sub">
          Compare LLM models on your comprehensive-check requirements — accuracy, cost, and performance side by side.
        </p>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-primary" @click="showCreateModal = true">
          <i class="fas fa-plus" aria-hidden="true"></i>
          New A/B test
        </button>
      </div>
    </header>

    <section class="purpose-panel">
      <h2>How it works</h2>
      <ol class="purpose-steps">
        <li>Create an experiment and choose models to compare.</li>
        <li>Source requirements from existing comprehensive checks on your projects.</li>
        <li>Run the same requirements through each model.</li>
        <li>Compare accuracy, cost, and latency — then apply insights.</li>
      </ol>
      <div class="quick-start-buttons">
        <button type="button" class="btn btn-ghost" @click="createQuickTest('claude_vs_gpt4')">
          <i class="fas fa-robot" aria-hidden="true"></i>
          Claude vs GPT-4
        </button>
        <button type="button" class="btn btn-ghost" @click="createQuickTest('all_models')">
          <i class="fas fa-flask" aria-hidden="true"></i>
          Compare all models
        </button>
        <button type="button" class="btn btn-ghost" @click="createAccuracyFocusedTest">
          <i class="fas fa-bullseye" aria-hidden="true"></i>
          Accuracy-focused test
        </button>
        <button
          v-if="draftExperiments.length > 3"
          type="button"
          class="btn btn-ghost btn-danger-text"
          @click="bulkDeleteDraftExperiments"
        >
          <i class="fas fa-trash" aria-hidden="true"></i>
          Clean up drafts ({{ draftExperiments.length }})
        </button>
      </div>
    </section>

    <!-- Experiments Grid -->
    <div class="experiments-section">
        
        <!-- Draft Experiments -->
        <div v-if="draftExperiments.length > 0" class="experiments-group">
          <h3><i class="fas fa-edit"></i> Draft Experiments ({{ draftExperiments.length }})</h3>
          <div class="experiments-grid">
            <div v-for="experiment in draftExperiments" :key="experiment.id" class="experiment-card draft">
              <div class="experiment-header">
                <h4>{{ experiment.name }}</h4>
                <span class="status-text">
                  {{ experiment.status }}
                </span>
              </div>
              
              <div class="experiment-body">
                <p class="description">{{ experiment.description }}</p>
                
                <div class="models-to-test">
                  <strong>Models to Compare:</strong>
                  <span v-for="model in experiment.models_to_compare" :key="model" 
                        class="model-badge" :class="model">
                    {{ getModelDisplayName(model) }}
                  </span>
                </div>
                
                <div class="experiment-config">
                  <div class="config-item">
                    <i class="fas fa-chart-bar"></i>
                    Sample Size: {{ experiment.sample_size_target }}
                  </div>
                  <div class="config-item" v-if="experiment.requirement_types.length">
                    <i class="fas fa-tags"></i>
                    Types: {{ experiment.requirement_types.join(', ') }}
                  </div>
                </div>
              </div>
              
              <div class="experiment-actions">
                <button @click="startExperiment(experiment.id)" class="btn btn-sm btn-success">
                  <i class="fas fa-play"></i>
                  Start Experiment
                </button>
                <button @click="viewExperimentDetails(experiment)" class="btn btn-sm btn-outline-primary">
                  View Details
                </button>
                <button @click="deleteExperiment(experiment.id)" class="btn btn-sm btn-outline-danger">
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Active Experiments -->
        <div v-if="activeExperiments.length > 0" class="experiments-group">
          <h3><i class="fas fa-play-circle"></i> Active Experiments ({{ activeExperiments.length }})</h3>
          <div class="experiments-grid">
            <div v-for="experiment in activeExperiments" :key="experiment.id" class="experiment-card active">
              <div class="experiment-header">
                <h4>{{ experiment.name }}</h4>
                <span class="status-badge" :class="experiment.status.toLowerCase()">
                  {{ experiment.status }}
                </span>
              </div>
              
              <div class="experiment-body">
                <p class="description">{{ experiment.description }}</p>
                
                <div class="progress-section">
                  <div class="progress-bar">
                    <div class="progress-fill" :style="{width: experiment.progress_percentage + '%'}"></div>
                  </div>
                  <span class="progress-text">{{ experiment.progress_percentage.toFixed(1) }}% complete</span>
                </div>
                
                <div class="models-being-tested">
                  <strong>Models:</strong>
                  <span v-for="model in experiment.models_to_compare" :key="model" 
                        class="model-badge" :class="model">
                    {{ getModelDisplayName(model) }}
                  </span>
                </div>
                
                <div class="experiment-stats">
                  <div class="stat">
                    <span class="stat-value">{{ experiment.total_tests_run }}</span>
                    <span class="stat-label">Tests Run</span>
                  </div>
                  <div class="stat">
                    <span class="stat-value">{{ experiment.sample_size_target * experiment.models_to_compare.length }}</span>
                    <span class="stat-label">Target Tests</span>
                  </div>
                </div>
              </div>
              
              <div class="experiment-actions">
                <button @click="viewExperimentDetails(experiment)" class="btn btn-sm btn-outline-primary">
                  View Details
                </button>
                <button v-if="experiment.status === 'RUNNING'" 
                        @click="pauseExperiment(experiment.id)" 
                        class="btn btn-sm btn-outline-warning">
                  Pause
                </button>
                <button v-if="experiment.status === 'PAUSED'" 
                        @click="resumeExperiment(experiment.id)" 
                        class="btn btn-sm btn-outline-success">
                  Resume
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Completed Experiments -->
        <div v-if="completedExperiments.length > 0" class="experiments-group">
          <h3><i class="fas fa-check-circle"></i> Completed Experiments</h3>
          <div class="experiments-grid">
            <div v-for="experiment in completedExperiments" :key="experiment.id" class="experiment-card completed">
              <div class="experiment-header">
                <h4>{{ experiment.name }}</h4>
                <span class="status-badge completed">
                  {{ experiment.status }}
                </span>
              </div>
              
              <div class="experiment-body">
                <p class="description">{{ experiment.description }}</p>
                
                <div class="completion-info">
                  <div class="completion-stat">
                    <i class="fas fa-calendar-check"></i>
                    Completed {{ formatDate(experiment.completed_at) }}
                  </div>
                  <div class="completion-stat">
                    <i class="fas fa-chart-bar"></i>
                    {{ experiment.total_tests_run }} tests completed
                  </div>
                </div>
                
                <div class="models-tested">
                  <strong>Models Compared:</strong>
                  <span v-for="model in experiment.models_to_compare" :key="model" 
                        class="model-badge" :class="model">
                    {{ getModelDisplayName(model) }}
                  </span>
                </div>
              </div>
              
              <div class="experiment-actions">
                <button @click="viewExperimentDetails(experiment)" class="btn btn-sm btn-primary">
                  View Results
                </button>
                <button @click="viewInsights(experiment)" class="btn btn-sm btn-outline-info">
                  View Insights
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Empty State -->
        <div v-if="experiments.length === 0 && !loading" class="empty-state">
          <div class="empty-icon">
            <i class="fas fa-flask"></i>
          </div>
          <h3>No A/B Tests Yet</h3>
          <p>Start comparing LLM models to optimize cost and accuracy</p>
          <button @click="showCreateModal = true" class="btn btn-primary btn-lg">
            Create Your First A/B Test
          </button>
        </div>
        
        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <i class="fas fa-spinner fa-spin"></i>
          <span>Loading experiments...</span>
        </div>
    </div>

    <!-- A/B Test Results Section -->
    <div class="results-wrap" v-if="!loading && experiments.length > 0">
        <div class="results-section">
        <div class="section-header">
          <h2><i class="fas fa-chart-line"></i> A/B Test Results</h2>
          <p class="text-muted">Detailed comparison results from your experiments</p>
          
          <!-- View Toggle -->
          <div class="view-toggle">
            <button 
              @click="resultsViewMode = 'all'" 
              :class="['btn', 'btn-sm', resultsViewMode === 'all' ? 'btn-primary' : 'btn-outline']">
              <i class="fas fa-list"></i> All Results
            </button>
            <button 
              @click="resultsViewMode = 'by-requirement'" 
              :class="['btn', 'btn-sm', resultsViewMode === 'by-requirement' ? 'btn-primary' : 'btn-outline']">
              <i class="fas fa-tasks"></i> By Requirement
            </button>
          </div>
        </div>

        <!-- All Results View -->
        <div v-if="resultsViewMode === 'all'" class="results-table-container">
          <table class="table table-striped">
            <thead>
              <tr>
                <th>Experiment</th>
                <th>Requirement</th>
                <th>Document</th>
                <th>Models</th>
                <th>Best Model</th>
                <th>Cost Savings</th>
                <th>Speed Improvement</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="result in allResults" :key="result.id" class="result-row">
                <td>
                  <strong>{{ result.experiment_name }}</strong>
                  <br>
                  <small class="text-muted">{{ result.requirement_type }}</small>
                </td>
                <td>
                  <div class="requirement-info">
                    {{ result.requirement_text?.substring(0, 50) }}...
                  </div>
                </td>
                <td>
                  <small>{{ result.document_name }}</small>
                </td>
                <td>
                  <div class="models-tested">
                    <span v-for="(modelResult, model) in result.model_results" 
                          :key="model" 
                          :class="`model-badge model-${model}`">
                      {{ getModelDisplayName(model) }}
                    </span>
                  </div>
                </td>
                <td>
                  <span class="best-model-badge">
                    {{ getBestModel(result.model_results) }}
                  </span>
                </td>
                <td>
                  <span class="cost-savings">
                    {{ getCostSavings(result.model_results) }}
                  </span>
                </td>
                <td>
                  <span class="speed-improvement">
                    {{ getSpeedImprovement(result.model_results) }}
                  </span>
                </td>
                <td>
                  <small>{{ new Date(result.created_at).toLocaleDateString() }}</small>
                </td>
              </tr>
            </tbody>
          </table>
          
          <!-- Pagination Controls -->
          <div v-if="resultsCount > resultsPageSize" class="pagination-controls">
            <div class="pagination-info">
              <span>Showing {{ allResults.length }} of {{ resultsCount }} results</span>
            </div>
            <div class="pagination-buttons">
              <button 
                @click="loadPreviousPage" 
                :disabled="currentResultsPage <= 1"
                class="btn btn-outline btn-sm">
                <i class="fas fa-chevron-left"></i> Previous
              </button>
              <span class="page-info">Page {{ currentResultsPage }}</span>
              <button 
                @click="loadNextPage" 
                :disabled="!hasMoreResults"
                class="btn btn-outline btn-sm">
                Next <i class="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
        
        <!-- By Requirement View -->
        <div v-if="resultsViewMode === 'by-requirement'" class="by-requirement-container">
          <div v-if="uniqueRequirements.length === 0" class="no-requirements">
            <i class="fas fa-search"></i>
            <p>No requirements found in current results</p>
          </div>
          
          <div v-else class="requirements-list">
            <div class="requirement-selector">
              <label>Select Requirement to Review:</label>
              <select v-model="selectedRequirementId" @change="loadRequirementResults" class="form-control">
                <option value="">Choose a requirement...</option>
                <option v-for="req in uniqueRequirements" :key="req.id" :value="req.id">
                  {{ req.text }} ({{ req.count }} tests)
                </option>
              </select>
            </div>
            
            <div v-if="selectedRequirementId && requirementResults.length > 0" class="requirement-results">
              <div class="requirement-header">
                <h4>{{ selectedRequirement?.text }}</h4>
                <p class="text-muted">{{ requirementResults.length }} test results for this requirement</p>
                <div class="bulk-actions">
                  <button @click="markAllCorrect" class="btn btn-success btn-sm">
                    <i class="fas fa-check-double"></i> Mark All Correct
                  </button>
                  <button @click="markAllWrong" class="btn btn-danger btn-sm">
                    <i class="fas fa-times-circle"></i> Mark All Wrong
                  </button>
                </div>
              </div>
              
              <div class="requirement-test-grid">
                <div v-for="result in requirementResults" :key="result.id" class="test-result-card">
                  <div class="card-header">
                    <h6>{{ result.document_name }}</h6>
                    <span class="experiment-badge">{{ result.experiment_name }}</span>
                  </div>
                  
                  <div class="model-results-grid">
                    <div v-for="(modelResult, model) in result.model_results" :key="model" class="model-result">
                      <div class="model-header">
                        <span class="model-name">{{ getModelDisplayName(model) }}</span>
                        <span :class="`result-badge result-${modelResult.result?.toLowerCase()}`">
                          {{ modelResult.result }}
                        </span>
                      </div>
                      
                      <div class="model-details">
                        <div class="confidence">
                          <i class="fas fa-chart-line"></i> 
                          {{ (modelResult.confidence * 100).toFixed(0) }}%
                        </div>
                        <div class="cost">
                          <i class="fas fa-dollar-sign"></i> 
                          ${{ modelResult.cost?.toFixed(4) || '0' }}
                        </div>
                      </div>
                      
                      <div class="reasoning-section">
                        <div class="reasoning-preview">
                          {{ truncateReasoning(modelResult.reasoning, 120) }}
                        </div>
                        <button 
                          @click="toggleMainReasoning(result.id, model)" 
                          class="expand-reasoning-btn"
                          v-if="modelResult.reasoning && modelResult.reasoning.length > 120">
                          <i class="fas fa-expand-alt"></i>
                          {{ isMainReasoningExpanded(result.id, model) ? 'Show Less' : 'Show Full Reasoning' }}
                        </button>
                        <div v-if="isMainReasoningExpanded(result.id, model)" class="full-reasoning-content">
                          <h6>Full Reasoning:</h6>
                          <p>{{ modelResult.reasoning }}</p>
                        </div>
                      </div>
                      
                      <div class="validation-controls">
                        <button 
                          @click="validateModelResult(result, model, 'CORRECT')"
                          :class="['validation-btn', 'correct-btn', { 'selected': getModelValidation(result, model) === 'CORRECT', 'has-feedback': hasModelFeedback(result, model) }]">
                          <i class="fas fa-check"></i> Correct
                        </button>
                        <button 
                          @click="validateModelResult(result, model, 'WRONG')"
                          :class="['validation-btn', 'wrong-btn', { 'selected': getModelValidation(result, model) === 'WRONG', 'has-feedback': hasModelFeedback(result, model) }]">
                          <i class="fas fa-times"></i> Wrong
                        </button>
                        <button 
                          @click="validateModelResult(result, model, 'PARTIAL')"
                          :class="['validation-btn', 'partial-btn', { 'selected': getModelValidation(result, model) === 'PARTIAL', 'has-feedback': hasModelFeedback(result, model) }]">
                          <i class="fas fa-edit"></i> Needs Correction
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Experiment Modal -->
    <div v-if="showCreateModal" class="modal-overlay" @click.self="showCreateModal = false">
      <div class="modal-container modal-large">
        <div class="modal-header">
          <h3><i class="fas fa-flask"></i> Create A/B Test Experiment</h3>
          <button @click="showCreateModal = false" class="modal-close">&times;</button>
        </div>
        
        <div class="modal-body">
          <form @submit.prevent="createExperiment" class="experiment-form">
            <div class="form-group">
              <label for="experimentName" class="form-label">Experiment Name</label>
              <input 
                id="experimentName"
                v-model="newExperiment.name" 
                type="text" 
                class="form-control"
                placeholder="e.g., Claude vs GPT-4 Cost Optimization"
                required
              />
            </div>
            
            <div class="form-group">
              <label for="experimentDescription" class="form-label">Description</label>
              <textarea 
                id="experimentDescription"
                v-model="newExperiment.description" 
                class="form-control"
                rows="3"
                placeholder="What are you testing and why?"
                required
              ></textarea>
            </div>
            
            <div class="form-group">
              <label for="sourceProject" class="form-label">Source Project (Optional)</label>
              <select 
                id="sourceProject"
                v-model="newExperiment.source_project" 
                class="form-control"
              >
                <option :value="null">Use requirements from any project</option>
                <option v-for="project in projects" :key="project.id" :value="project.id">
                  {{ project.name }} ({{ project.project_type }})
                </option>
              </select>
              <small class="form-text text-muted">
                Choose a specific project to source requirements from, or leave blank to use requirements from all projects
              </small>
            </div>
            
            <div class="form-group">
              <label class="form-label">Models to Compare</label>
              <div class="model-selection">
                <div class="model-category">
                  <h6>OpenAI Models</h6>
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="newExperiment.models_to_compare" value="gpt-4-turbo" />
                    <span class="checkmark openai"></span>
                    🤖 GPT-4 Turbo (Best reasoning)
                  </label>
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="newExperiment.models_to_compare" value="gpt-4o" />
                    <span class="checkmark openai"></span>
                    🔥 GPT-4o (Latest, multimodal)
                  </label>
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="newExperiment.models_to_compare" value="gpt-3.5-turbo" />
                    <span class="checkmark openai"></span>
                    ⚡ GPT-3.5 Turbo (Fast & cheap)
                  </label>
                </div>
                
                <div class="model-category">
                  <h6>Anthropic Models</h6>
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="newExperiment.models_to_compare" value="claude-3.5-sonnet" />
                    <span class="checkmark claude"></span>
                    🧠 Claude 3.5 Sonnet (Latest, best)
                  </label>
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="newExperiment.models_to_compare" value="claude-3-sonnet" />
                    <span class="checkmark claude"></span>
                    🎯 Claude 3 Sonnet (Balanced)
                  </label>
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="newExperiment.models_to_compare" value="claude-3-haiku" />
                    <span class="checkmark claude"></span>
                    💨 Claude 3 Haiku (Fast & cheap)
                  </label>
                </div>
                
                <div class="model-category">
                  <h6>X.AI Models</h6>
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="newExperiment.models_to_compare" value="grok-2-1212" />
                    <span class="checkmark grok"></span>
                    ⚡ Grok 2 (X.AI)
                  </label>
                </div>
                
                <div class="model-category">
                  <h6>Google Models</h6>
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="newExperiment.models_to_compare" value="gemini-2.5-pro" />
                    <span class="checkmark gemini"></span>
                    💎 Gemini 2.5 Pro (Latest, best reasoning)
                  </label>
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="newExperiment.models_to_compare" value="gemini-2.5-flash" />
                    <span class="checkmark gemini"></span>
                    ⚡ Gemini 2.5 Flash (Fast & cheap)
                  </label>
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="newExperiment.models_to_compare" value="gemini-1.5-flash" />
                    <span class="checkmark gemini"></span>
                    💨 Gemini Flash Latest (Stable)
                  </label>
                </div>
              </div>
            </div>
            
            <div class="form-group">
              <label for="baselineModel" class="form-label">Baseline Model</label>
              <select id="baselineModel" v-model="newExperiment.baseline_model" class="form-control" required>
                <option value="">Select baseline model...</option>
                <option v-for="model in newExperiment.models_to_compare" :key="model" :value="model">
                  {{ getModelDisplayName(model) }}
                </option>
              </select>
            </div>
            
            <div class="form-group">
              <label class="form-label">Project Types to Test</label>
              <div class="requirement-type-selection">
                <label class="checkbox-label">
                  <input type="checkbox" v-model="newExperiment.requirement_types" value="COMPLIANCE" />
                  <span class="checkmark compliance"></span>
                  📋 Compliance Reviews
                </label>
                <label class="checkbox-label">
                  <input type="checkbox" v-model="newExperiment.requirement_types" value="CERT_REVIEW" />
                  <span class="checkmark cert"></span>
                  📜 Certificate Reviews
                </label>
                <label class="checkbox-label">
                  <input type="checkbox" v-model="newExperiment.requirement_types" value="RISK_REVIEW_INS" />
                  <span class="checkmark risk"></span>
                  ⚠️ Insurance Risk Reviews
                </label>
              </div>
              <small class="form-text text-muted">
                Select which types of projects to source requirements from
              </small>
            </div>
            
            <div class="form-group">
              <label class="form-label">Comprehensive Checks to Test</label>
              <div class="comprehensive-checks-selection">
                <div v-if="comprehensiveChecks.length === 0" class="text-muted">
                  Loading comprehensive checks...
                </div>
                <div v-else class="checks-grid">
                  <label v-for="check in comprehensiveChecks" :key="check.id" class="checkbox-label check-item">
                    <input 
                      type="checkbox" 
                      v-model="newExperiment.comprehensive_checks" 
                      :value="check.id" 
                    />
                    <span class="checkmark check"></span>
                    <div class="check-info">
                      <strong>{{ check.name }}</strong>
                      <small class="text-muted">{{ check.project_type }} • {{ check.requirements?.length || 0 }} requirements</small>
                    </div>
                  </label>
                </div>
              </div>
              <small class="form-text text-muted">
                Choose specific comprehensive check definitions to test. Each check contains multiple requirements that will be tested across different LLM models.
              </small>
            </div>
            
            <div class="form-row">
              <div class="form-group">
                <label for="sampleSize" class="form-label">Sample Size</label>
                <input 
                  id="sampleSize"
                  v-model.number="newExperiment.sample_size_target" 
                  type="number" 
                  class="form-control"
                  min="10"
                  max="500"
                  required
                />
              </div>
              
              <div class="form-group">
                <label for="accuracyThreshold" class="form-label">Accuracy Threshold</label>
                <input 
                  id="accuracyThreshold"
                  v-model.number="newExperiment.accuracy_threshold" 
                  type="number" 
                  class="form-control"
                  step="0.01"
                  min="0.5"
                  max="1.0"
                  required
                />
              </div>
            </div>
            
            <div class="form-actions">
              <button type="button" @click="showCreateModal = false" class="btn btn-secondary">
                Cancel
              </button>
              <button type="submit" class="btn btn-primary" :disabled="!canCreateExperiment">
                Create Experiment
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    
    <!-- Experiment Details Modal -->
    <div v-if="showDetailsModal && selectedExperiment" class="modal-overlay" @click.self="showDetailsModal = false">
      <div class="modal-container modal-xl">
        <div class="modal-header">
          <h3><i class="fas fa-chart-line"></i> {{ selectedExperiment.name }}</h3>
          <button @click="showDetailsModal = false" class="modal-close">&times;</button>
        </div>
        
        <div class="modal-body">
          <div class="experiment-details">
            <!-- Progress Overview -->
            <div class="progress-overview">
              <div class="progress-card">
                <h4>Progress</h4>
                <div class="progress-circle">
                  <span class="progress-percentage">{{ selectedExperiment.progress_percentage.toFixed(0) }}%</span>
                </div>
                <div class="progress-stats">
                  <div>{{ selectedExperiment.total_tests_run }} / {{ selectedExperiment.sample_size_target * selectedExperiment.models_to_compare.length }} tests</div>
                </div>
              </div>
              
              <!-- Model Comparison -->
              <div class="model-comparison" v-if="experimentAnalytics">
                <h4>Model Performance</h4>
                <div class="model-performance-grid">
                  <div v-for="(stats, model) in experimentAnalytics.cost_analysis" :key="model" 
                       class="model-performance-card" :class="model">
                    <div class="model-name">{{ getModelDisplayName(model) }}</div>
                    <div class="performance-metrics">
                      <div class="metric">
                        <span class="metric-value">${{ stats.avg_cost.toFixed(4) }}</span>
                        <span class="metric-label">Avg Cost</span>
                      </div>
                      <div class="metric" v-if="experimentAnalytics.accuracy_analysis[model].avg_accuracy !== null">
                        <span class="metric-value">{{ (experimentAnalytics.accuracy_analysis[model].avg_accuracy * 100).toFixed(1) }}%</span>
                        <span class="metric-label">Accuracy</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Enhanced Test Results -->
            <div v-if="experimentResults.length > 0" class="enhanced-results-section">
              <div class="results-header">
                <h4><i class="fas fa-microscope"></i> Test Results ({{ experimentResults.length }})</h4>
                <div class="view-and-filter-controls">
                  <!-- View Mode Toggle -->
                  <div class="view-mode-toggle">
                    <button @click="experimentViewMode = 'all'" 
                            :class="{'active': experimentViewMode === 'all'}" 
                            class="view-btn">
                      <i class="fas fa-list"></i> All Results
                    </button>
                    <button @click="experimentViewMode = 'by-requirement'" 
                            :class="{'active': experimentViewMode === 'by-requirement'}" 
                            class="view-btn">
                      <i class="fas fa-tasks"></i> By Requirement
                    </button>
                  </div>
                  
                  <!-- Filters (only show in 'all' mode) -->
                  <div v-if="experimentViewMode === 'all'" class="results-filters">
                    <button @click="resultsFilter = 'all'" 
                            :class="{'active': resultsFilter === 'all'}" 
                            class="filter-btn">
                      All ({{ experimentResults.length }})
                    </button>
                    <button @click="resultsFilter = 'disagreements'" 
                            :class="{'active': resultsFilter === 'disagreements'}" 
                            class="filter-btn disagreement">
                      Disagreements ({{ getDisagreementCount() }})
                    </button>
                    <button @click="resultsFilter = 'needsValidation'" 
                            :class="{'active': resultsFilter === 'needsValidation'}" 
                            class="filter-btn validation">
                      Needs Validation ({{ getNeedsValidationCount() }})
                    </button>
                  </div>
                </div>
              </div>
              
              <!-- All Results View (existing) -->
              <div v-if="experimentViewMode === 'all'" class="enhanced-results-grid">
                <div v-for="result in getFilteredResults()" :key="result.id" 
                     class="enhanced-result-card" 
                     :class="{
                       'models-disagree': !checkModelsAgree(result.model_results),
                       'needs-validation': !result.human_validation,
                       'validated': result.human_validation
                     }">
                  
                  <!-- Result Card Header -->
                  <div class="result-card-header">
                    <div class="result-title">
                      <h6>{{ result.requirement_type.replace('_', ' ').toUpperCase() }}</h6>
                      <div class="requirement-identifier">
                        <span class="req-code">{{ result.requirement_id }}</span>
                        <span class="req-name">{{ result.requirement_text?.substring(0, 60) }}{{ result.requirement_text?.length > 60 ? '...' : '' }}</span>
                      </div>
                      <div class="status-indicators">
                        <span v-if="!checkModelsAgree(result.model_results)" class="status-badge disagreement" title="Models disagree">
                          <i class="fas fa-exclamation-triangle"></i> Disagreement
                        </span>
                        <span v-if="result.human_validation" class="status-badge validated" title="Human validated">
                          <i class="fas fa-check-circle"></i> Validated
                        </span>
                        <span v-else class="status-badge needs-validation" title="Needs human validation">
                          <i class="fas fa-clock"></i> Needs Review
                        </span>
                      </div>
                    </div>
                    <div class="document-info">
                      <small class="document-name">📄 {{ result.document_name }}</small>
                    </div>
                  </div>
                  
                  <!-- Requirement Details -->
                  <div class="requirement-summary">
                    <div class="requirement-text">
                      <strong>What we're checking:</strong>
                      <p>{{ result.requirement_text }}</p>
                    </div>
                  </div>
                  
                  <!-- Model Results Comparison -->
                  <div class="model-results-comparison">
                    <div class="comparison-header">
                      <h6>Model Results</h6>
                      <div class="agreement-indicator" :class="{'agree': checkModelsAgree(result.model_results), 'disagree': !checkModelsAgree(result.model_results)}">
                        <i :class="checkModelsAgree(result.model_results) ? 'fas fa-handshake' : 'fas fa-times-circle'"></i>
                        {{ checkModelsAgree(result.model_results) ? 'Models Agree' : 'Models Disagree' }}
                        <!-- Debug info -->
                        <small v-if="!checkModelsAgree(result.model_results)" class="debug-results">
                          ({{ getModelResultsDebug(result.model_results) }})
                        </small>
                      </div>
                    </div>
                    
                    <div class="models-grid">
                      <div v-for="(modelResult, model) in result.model_results" 
                           :key="model" 
                           class="model-result-card"
                           :class="`model-${model} result-${(modelResult.result || 'unclear').toLowerCase()}`">
                        
                        <div class="model-result-header">
                          <div class="model-info">
                            <span class="model-name">{{ getModelDisplayName(model) }}</span>
                            <span class="model-icon">{{ getModelIcon(model) }}</span>
                          </div>
                          <div class="result-decision" :class="`decision-${(modelResult.result || 'unclear').toLowerCase()}`">
                            <i :class="getResultIcon(modelResult.result)"></i>
                            <span class="decision-text">{{ modelResult.result || 'UNCLEAR' }}</span>
                          </div>
                        </div>
                        
                        <!-- Model's Conclusion/Result -->
                        <div class="model-conclusion">
                          <div class="conclusion-header">
                            <strong>{{ getModelDisplayName(model) }}'s Conclusion:</strong>
                          </div>
                          <div class="conclusion-content" :class="`conclusion-${(modelResult.result || 'unclear').toLowerCase()}`">
                            <div class="conclusion-result">
                              <span class="result-label">Decision:</span>
                              <span class="result-value" :class="`result-${(modelResult.result || 'unclear').toLowerCase()}`">
                                {{ formatModelResult(modelResult.result) }}
                              </span>
                            </div>
                            <div v-if="modelResult.reasoning" class="conclusion-reasoning">
                              <span class="reasoning-label">Reasoning Summary:</span>
                              <div class="reasoning-summary-container">
                                <p class="reasoning-summary" 
                                   :class="{'expanded': isReasoningSummaryExpanded(result.id, model)}">
                                  {{ isReasoningSummaryExpanded(result.id, model) ? modelResult.reasoning : getReasoningSummary(modelResult.reasoning) }}
                                </p>
                                <button v-if="modelResult.reasoning.length > 150" 
                                        @click="toggleReasoningSummary(result.id, model)"
                                        class="expand-summary-btn">
                                  {{ isReasoningSummaryExpanded(result.id, model) ? 'Show Less' : 'Show More' }}
                                </button>
                              </div>
                            </div>
                            <div v-if="modelResult.evidence" class="conclusion-evidence">
                              <span class="evidence-label">Evidence Found:</span>
                              <p class="evidence-text">{{ modelResult.evidence }}</p>
                            </div>
                          </div>
                        </div>
                        
                        <div class="model-metrics">
                          <div class="metric-row">
                            <span class="metric-label">Confidence:</span>
                            <div class="confidence-bar">
                              <div class="confidence-fill" 
                                   :style="{width: ((modelResult.confidence || 0) * 100) + '%'}"
                                   :class="getConfidenceClass(modelResult.confidence)">
                              </div>
                              <span class="confidence-text">{{ ((modelResult.confidence || 0) * 100).toFixed(1) }}%</span>
                            </div>
                          </div>
                          <div class="metric-row">
                            <span class="metric-label">Cost:</span>
                            <span class="metric-value cost">${{ (modelResult.cost || 0).toFixed(4) }}</span>
                          </div>
                          <div class="metric-row">
                            <span class="metric-label">Time:</span>
                            <span class="metric-value time">{{ (modelResult.processing_time || 0).toFixed(2) }}s</span>
                          </div>
                        </div>
                        
                        <div class="reasoning-preview" v-if="modelResult.reasoning">
                          <button @click="toggleReasoning(result.id, model)" 
                                  class="reasoning-toggle">
                            <i class="fas fa-brain"></i>
                            {{ getReasoningToggleText(result.id, model) }}
                          </button>
                          <div v-if="isReasoningExpanded(result.id, model)" class="reasoning-content">
                            <p>{{ modelResult.reasoning }}</p>
                          </div>
                        </div>
                        
                        <!-- Model-Specific Validation Buttons -->
                        <div class="model-validation-actions">
                          <button @click="validateModelResult(result, model, 'CORRECT')" 
                                  class="model-action-btn validate-correct"
                                  :class="{'active': getModelValidation(result, model) === 'CORRECT', 'loading': isValidating(result.id, model)}"
                                  :disabled="isValidating(result.id, model)"
                                  title="This model's conclusion is completely correct">
                            <i v-if="isValidating(result.id, model)" class="fas fa-spinner fa-spin"></i>
                            <i v-else class="fas fa-check"></i> 
                            Perfect
                          </button>
                          <button @click="validateModelResult(result, model, 'PARTIAL')" 
                                  class="model-action-btn validate-partial"
                                  :class="{'active': getModelValidation(result, model) === 'PARTIAL', 'loading': isValidating(result.id, model)}"
                                  :disabled="isValidating(result.id, model)"
                                  title="Right conclusion but reasoning needs improvement - Click to provide detailed correction">
                            <i v-if="isValidating(result.id, model)" class="fas fa-spinner fa-spin"></i>
                            <i v-else class="fas fa-edit"></i>
                            Right but Needs Correction
                          </button>
                          <button @click="validateModelResult(result, model, 'WRONG')" 
                                  class="model-action-btn validate-wrong"
                                  :class="{'active': getModelValidation(result, model) === 'WRONG', 'loading': isValidating(result.id, model)}"
                                  :disabled="isValidating(result.id, model)"
                                  title="This model's conclusion is completely wrong">
                            <i v-if="isValidating(result.id, model)" class="fas fa-spinner fa-spin"></i>
                            <i v-else class="fas fa-times"></i>
                            Wrong
                          </button>
                          
                          <!-- Feedback Indicator -->
                          <div class="feedback-indicator" v-if="getModelValidation(result, model) === 'PARTIAL' || getModelValidation(result, model) === 'WRONG'">
                            <span class="feedback-badge" 
                                  @click="openCorrectionModal(result, model)"
                                  title="Click to view/edit detailed correction">
                              <i class="fas fa-comment-dots"></i>
                              {{ correctionData.isEditingExisting && correctionData.result?.id === result.id && correctionData.model === model ? 'Edit' : 'View' }} Correction
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Document Viewer Section -->
                  <div class="document-viewer-section">
                    <div class="document-viewer-header">
                      <button @click="toggleDocumentViewer(result.id)" 
                              class="document-toggle-btn"
                              :class="{'expanded': isDocumentViewerExpanded(result.id)}">
                        <i class="fas fa-file-pdf"></i>
                        {{ isDocumentViewerExpanded(result.id) ? 'Hide Document' : 'Show Document' }}
                        <i :class="isDocumentViewerExpanded(result.id) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                      </button>
                      <small class="document-name-small">📄 {{ result.document_name }}</small>
                    </div>
                    
                    <div v-if="isDocumentViewerExpanded(result.id)" class="document-viewer-content">
                      <div class="document-preview">
                        <div v-if="result.fetchingDocument" class="loading-preview">
                          <i class="fas fa-spinner fa-spin"></i>
                          <p>Loading document...</p>
                        </div>
                        <div v-else-if="getDocumentUrlForResult(result)" class="pdf-container">
                          <iframe :src="getDocumentUrlForResult(result)" 
                                  type="application/pdf"
                                  class="pdf-preview"
                                  title="Document Preview"
                                  @load="onDocumentLoad(result.id)"
                                  @error="onDocumentError(result.id)">
                          </iframe>
                          <div class="pdf-fallback">
                            <p><i class="fas fa-file-pdf"></i> PDF Viewer</p>
                            <p class="small text-muted">
                              If the PDF doesn't display above, 
                              <a :href="getDocumentUrlForResult(result)" target="_blank">open in new tab</a>
                            </p>
                          </div>
                        </div>
                        <div v-else class="no-preview">
                          <i class="fas fa-file"></i>
                          <p>Document preview not available</p>
                          <small>Document ID: {{ result.document_id }}</small>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Action Buttons - Overall validations removed, use model-specific validations only -->
                  <div class="result-actions">
                    <div class="winner-indicator" v-if="checkModelsAgree(result.model_results)">
                      <span class="winner-text">✅ Consistent Result</span>
                    </div>
                    <div class="winner-indicator disagreement" v-else>
                      <span class="winner-text">⚠️ Needs Human Review</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Insights -->
            <div v-if="experimentInsights.length > 0" class="insights-section">
              <h4><i class="fas fa-lightbulb"></i> Key Insights</h4>
              <div class="insights-grid">
                <div v-for="insight in experimentInsights" :key="insight.id" 
                     class="insight-card" :class="insight.insight_type.toLowerCase()">
                  <div class="insight-header">
                    <h5>{{ insight.title }}</h5>
                    <span class="confidence-badge">{{ (insight.confidence_level * 100).toFixed(0) }}% confidence</span>
                  </div>
                  <p class="insight-description">{{ insight.description }}</p>
                  <div v-if="insight.recommended_action" class="insight-action">
                    <strong>Recommended Action:</strong> {{ insight.recommended_action }}
                  </div>
                  <div v-if="insight.potential_savings" class="insight-savings">
                    <strong>Potential Savings:</strong> ${{ insight.potential_savings }}
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Accuracy Analysis Section -->
            <div v-if="experimentAccuracyAnalysis" class="accuracy-analysis-section">
              <h4><i class="fas fa-bullseye"></i> Accuracy Analysis</h4>
              <div class="accuracy-overview">
                <div class="accuracy-metrics">
                  <div class="metric-card" v-if="experimentAccuracyAnalysis.recommended_model">
                    <h5>🎯 Recommended Model</h5>
                    <div class="recommended-model">
                      {{ getModelDisplayName(experimentAccuracyAnalysis.recommended_model) }}
                    </div>
                  </div>
                  
                  <div class="metric-card" v-if="experimentValidationStatus">
                    <h5>📊 Validation Progress</h5>
                    <div class="validation-progress">
                      <div class="progress-bar">
                        <div class="progress-fill" 
                             :style="{width: (experimentValidationStatus.validation_rate * 100) + '%'}">
                        </div>
                      </div>
                      <span>{{ (experimentValidationStatus.validation_rate * 100).toFixed(1) }}% validated</span>
                      <small>({{ experimentValidationStatus.validated_results }}/{{ experimentValidationStatus.total_results }})</small>
                    </div>
                  </div>
                </div>
                
                <div v-if="experimentAccuracyAnalysis.model_scores" class="model-accuracy-comparison">
                  <h5>Model Accuracy Comparison</h5>
                  <div class="accuracy-grid">
                    <div v-for="(scores, model) in experimentAccuracyAnalysis.model_scores" 
                         :key="model" 
                         class="model-accuracy-card">
                      <div class="model-header">
                        <h6>{{ getModelDisplayName(model) }}</h6>
                        <span class="accuracy-score">{{ (scores.accuracy * 100).toFixed(1) }}%</span>
                      </div>
                      <div class="accuracy-details">
                        <div class="accuracy-metric">
                          <span class="metric-label">Weighted Score:</span>
                          <span class="metric-value">{{ scores.weighted_score.toFixed(3) }}</span>
                        </div>
                        <div class="accuracy-metric">
                          <span class="metric-label">Avg Cost:</span>
                          <span class="metric-value">${{ scores.avg_cost.toFixed(4) }}</span>
                        </div>
                        <div class="accuracy-metric">
                          <span class="metric-label">Sample Size:</span>
                          <span class="metric-value">{{ scores.sample_size }}</span>
                        </div>
                        <div class="accuracy-metric">
                          <span class="metric-label">Human Validated:</span>
                          <span class="metric-value">{{ scores.human_validated_count }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Correction Modal -->
    <div v-if="showCorrectionModal" class="modal-overlay" @click.self="closeCorrectionModal">
      <div class="modal-container modal-lg">
        <div class="modal-header">
          <h3>
            <i class="fas fa-edit"></i> 
            {{ correctionData.isEditingExisting ? 'Edit' : 'Provide' }} 
            {{ getModelValidation(correctionData.result, correctionData.model) === 'WRONG' ? 'Correction' : 'Improvement' }} 
            for {{ getModelDisplayName(correctionData.model) }}
          </h3>
          <div class="feedback-status" v-if="correctionData.isEditingExisting">
            <span class="badge bg-info">
              <i class="fas fa-history"></i> Editing Existing Feedback
            </span>
          </div>
          <button @click="closeCorrectionModal" class="modal-close">&times;</button>
        </div>
        
        <div class="modal-body">
          <div class="correction-form">
            <div class="current-response-section">
              <h5>{{ getModelDisplayName(correctionData.model) }}'s Response:</h5>
              <div class="current-response">
                <div class="response-item">
                  <strong>Decision:</strong> {{ correctionData.result?.model_results[correctionData.model]?.result }}
                </div>
                <div class="response-item">
                  <strong>Current Reasoning:</strong>
                  <p class="current-reasoning">{{ correctionData.result?.model_results[correctionData.model]?.reasoning }}</p>
                </div>
              </div>
            </div>
            
            <div class="correction-inputs">
              <div class="form-group">
                <label for="correctReasoning"><strong>What should the reasoning be?</strong></label>
                <textarea 
                  id="correctReasoning"
                  v-model="correctionData.correctReasoning" 
                  class="form-control"
                  rows="4"
                  placeholder="Provide the correct reasoning that the model should have given..."></textarea>
                <small class="form-text">Example: 'The document provided is a Certificate of Liability Insurance for GHWWB, LLC - ACT Stuart LLC, located at 2251 Landmark Place, Manasquan, NJ'</small>
              </div>
              
              <div class="form-group">
                <label for="correctAnswer"><strong>What should the correct answer be?</strong></label>
                <select 
                  id="correctAnswer"
                  v-model="correctionData.correctAnswer" 
                  class="form-control">
                  <option value="">Select correct answer...</option>
                  <option value="PASS">PASS</option>
                  <option value="FAIL">FAIL</option>
                  <option value="UNCLEAR">UNCLEAR</option>
                </select>
              </div>
              
              <div class="form-group">
                <label for="correctEvidence"><strong>What evidence should it have found?</strong></label>
                <textarea 
                  id="correctEvidence"
                  v-model="correctionData.correctEvidence" 
                  class="form-control"
                  rows="3"
                  placeholder="Provide the specific evidence the model should have identified..."></textarea>
              </div>
              
              <div class="form-group">
                <label><strong>What type of issue is this?</strong></label>
                <div class="issue-categories">
                  <label v-for="category in availableIssueCategories" :key="category.value" class="checkbox-label">
                    <input 
                      type="checkbox" 
                      :value="category.value"
                      v-model="correctionData.issueCategories">
                    {{ category.label }}
                  </label>
                </div>
              </div>
              
              <div class="form-group">
                <label for="accuracyRating"><strong>Accuracy Rating</strong></label>
                <select 
                  id="accuracyRating"
                  v-model="correctionData.accuracyRating" 
                  class="form-control">
                  <option value="">Select accuracy...</option>
                  <option value="PERFECT">Perfect - Exactly right</option>
                  <option value="GOOD">Good - Minor issues</option>
                  <option value="ACCEPTABLE">Acceptable - Some concerns</option>
                  <option value="POOR">Poor - Major issues</option>
                  <option value="WRONG">Wrong - Completely incorrect</option>
                </select>
              </div>
              
              <div class="form-group">
                <label for="reasoningQuality"><strong>Reasoning Quality</strong></label>
                <select 
                  id="reasoningQuality"
                  v-model="correctionData.reasoningQuality" 
                  class="form-control">
                  <option value="">Select reasoning quality...</option>
                  <option value="EXCELLENT">Excellent - Clear and logical</option>
                  <option value="GOOD">Good - Mostly sound</option>
                  <option value="UNCLEAR">Unclear - Hard to follow</option>
                  <option value="FLAWED">Flawed - Logical errors</option>
                  <option value="MISSING">Missing - No reasoning provided</option>
                </select>
              </div>
              
              <div class="form-group">
                <label for="evidenceQuality"><strong>Evidence Quality</strong></label>
                <select 
                  id="evidenceQuality"
                  v-model="correctionData.evidenceQuality" 
                  class="form-control">
                  <option value="">Select evidence quality...</option>
                  <option value="PERFECT">Perfect - Exact relevant quotes</option>
                  <option value="GOOD">Good - Relevant but could be better</option>
                  <option value="PARTIAL">Partial - Some evidence missing</option>
                  <option value="WEAK">Weak - Evidence not convincing</option>
                  <option value="NONE">None - No evidence provided</option>
                </select>
              </div>
              
              <div class="form-group">
                <label for="suggestedImprovement"><strong>Suggested Improvement</strong></label>
                <textarea 
                  id="suggestedImprovement"
                  v-model="correctionData.suggestedImprovement" 
                  class="form-control"
                  rows="2"
                  placeholder="How can this model improve its performance?"></textarea>
              </div>
              
              <div class="form-group">
                <label for="specificIssues"><strong>Additional Notes</strong></label>
                <textarea 
                  id="specificIssues"
                  v-model="correctionData.specificIssues" 
                  class="form-control"
                  rows="2"
                  placeholder="Any additional details about what went wrong..."></textarea>
              </div>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button @click="closeCorrectionModal" class="btn btn-secondary">Cancel</button>
          <button @click="submitCorrection" class="btn btn-primary">
            <i class="fas fa-save"></i> 
            {{ correctionData.isEditingExisting ? 'Update Correction' : 'Save Correction' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from 'vue-toastification';
import { reactive } from 'vue';
import api from '@/services/api';

export default {
  name: 'ABTestingDashboard',
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      experiments: [],
      projects: [],
      comprehensiveChecks: [],
      allResults: [],
      experimentResults: [],
      loading: true,
      // Pagination for results
      resultsCount: 0,
      hasMoreResults: false,
      currentResultsPage: 1,
      resultsPageSize: 50,
      // View mode and requirement-focused data
      resultsViewMode: 'all', // 'all' or 'by-requirement'
      selectedRequirementId: '',
      requirementResults: [],
      // Experiment modal requirement view
      experimentViewMode: 'all', // 'all' or 'by-requirement'
      selectedExperimentRequirementId: '',
      experimentRequirementResults: [],
      showDetailsModal: false,
      selectedExperiment: null,
      showCreateModal: false,
      experimentAnalytics: null,
      experimentInsights: [],
      experimentAccuracyAnalysis: null,
      experimentValidationStatus: null,
      statusPollingInterval: null,
      resultsFilter: 'all',
      expandedReasoning: {},
      expandedReasoningSummary: {}, // Track expanded reasoning summaries
      modelValidations: reactive({}), // Track individual model validations
      validatingModels: {}, // Track which models are currently being validated
      expandedDocumentViewers: {}, // Track which document viewers are expanded
      showCorrectionModal: false,
      correctionData: {
        result: null,
        model: null,
        correctReasoning: '',
        correctEvidence: '',
        issueCategories: [],
        specificIssues: '',
        correctAnswer: '',
        accuracyRating: '',
        reasoningQuality: '',
        evidenceQuality: '',
        suggestedImprovement: '',
        existingFeedbackId: null,
        isEditingExisting: false
      },
      
      newExperiment: {
        name: '',
        description: '',
        models_to_compare: ['openai'],
        baseline_model: 'openai',
        sample_size_target: 50,
        accuracy_threshold: 0.95,
        max_cost_increase: 0.10,
        requirement_types: [],
        source_project: null,
        comprehensive_checks: [],
        project: null
      }
    };
  },
  
  computed: {
    availableIssueCategories() {
      return [
        { value: 'OVERTHINKING', label: 'Overthinking - Over-analyzed simple case' },
        { value: 'UNDERTHINKING', label: 'Underthinking - Missed important details' },
        { value: 'ENTITY_CONFUSION', label: 'Entity Confusion - Wrong entity identification' },
        { value: 'POLICY_MISREAD', label: 'Policy Misread - Misinterpreted coverage' },
        { value: 'FORMAT_ISSUE', label: 'Format Issue - Document format confusion' },
        { value: 'CONTEXT_MISSING', label: 'Context Missing - Missed important context' },
        { value: 'FALSE_POSITIVE', label: 'False Positive - Incorrectly flagged as passing' },
        { value: 'FALSE_NEGATIVE', label: 'False Negative - Incorrectly flagged as failing' }
      ];
    },
    activeExperiments() {
      return this.experiments.filter(exp => 
        ['RUNNING', 'PAUSED'].includes(exp.status)
      );
    },
    
    completedExperiments() {
      return this.experiments.filter(exp => 
        exp.status === 'COMPLETED'
      );
    },
    
    draftExperiments() {
      return this.experiments.filter(exp => 
        exp.status === 'DRAFT'
      );
    },
    
    canCreateExperiment() {
      return this.newExperiment.name && 
             this.newExperiment.description &&
             this.newExperiment.models_to_compare.length >= 2 &&
             this.newExperiment.baseline_model;
    },
    
    // Group results by requirement for requirement-focused view
    uniqueRequirements() {
      const requirementMap = new Map();
      
      for (const result of this.allResults) {
        const reqId = result.requirement_id;
        const reqText = result.requirement_text;
        
        if (requirementMap.has(reqId)) {
          requirementMap.get(reqId).count++;
        } else {
          requirementMap.set(reqId, {
            id: reqId,
            text: reqText,
            count: 1
          });
        }
      }
      
      return Array.from(requirementMap.values()).sort((a, b) => b.count - a.count);
    },
    
    selectedRequirement() {
      return this.uniqueRequirements.find(req => req.id === this.selectedRequirementId);
    }
  },
  
  async mounted() {
    await this.loadExperiments();
    await this.loadProjects();
    await this.loadComprehensiveChecks();
    await this.loadAllResults();
    
    // Start polling for running experiments
    this.startStatusPolling();
  },
  
  beforeUnmount() {
    // Clean up polling interval
    if (this.statusPollingInterval) {
      clearInterval(this.statusPollingInterval);
    }
  },
  
  methods: {
    async loadExperiments() {
      try {
        this.loading = true;
        const response = await api.get('/ab-testing/experiments/');
        console.log('Loaded experiments response:', response.data);
        
        // Check for status changes and notify
        if (this.experiments.length > 0) {
          this.checkForStatusChanges(response.data);
        }
        
        this.experiments = response.data;
        console.log('Experiments set to:', this.experiments);
        console.log('Draft experiments:', this.draftExperiments);
        console.log('Active experiments:', this.activeExperiments);
      } catch (error) {
        console.error('Error loading A/B test experiments:', error);
        this.toast.error('Failed to load experiments');
      } finally {
        this.loading = false;
      }
    },
    
    checkForStatusChanges(newExperiments) {
      // Check if any experiments changed from RUNNING to COMPLETED
      for (const newExp of newExperiments) {
        const oldExp = this.experiments.find(exp => exp.id === newExp.id);
        if (oldExp && oldExp.status === 'RUNNING' && newExp.status === 'COMPLETED') {
          this.toast.success(`🎉 A/B Test "${newExp.name}" completed! Click View Details to see results.`);
        }
      }
    },
    
    async loadProjects() {
      try {
        const response = await api.get('/projects/');
        console.log('Projects loaded:', response.data);
        // The API returns {projects: [...], count: 2, success: true}
        this.projects = response.data.projects || response.data;
        console.log('Projects set to:', this.projects);
      } catch (error) {
        console.error('Error loading projects:', error);
        // Don't show error toast for this - it's not critical
      }
    },
    
    async loadComprehensiveChecks() {
      try {
        const response = await api.get('/comprehensive-check-definitions/');
        console.log('Comprehensive checks loaded:', response.data);
        this.comprehensiveChecks = response.data;
      } catch (error) {
        console.error('Error loading comprehensive checks:', error);
        // Don't show error toast for this - it's not critical
      }
    },
    
    async createQuickTest(template) {
      try {
        // Create quick test templates based on the template type
        let quickTestData = {};
        
        if (template === 'claude_vs_gpt4') {
          quickTestData = {
            name: 'Claude vs GPT-4 Quick Test',
            description: 'Quick comparison between Claude 3 Haiku and GPT-4 for cost optimization',
            models_to_compare: ['claude', 'openai'],
            requirement_types: ['COMPLIANCE'],
            sample_size_target: 30,
            baseline_model: 'openai',
            source_project: null // Will use requirements from any project
          };
        } else if (template === 'all_models') {
          quickTestData = {
            name: 'All Models Comparison',
            description: 'Comprehensive comparison of all available LLM models',
            models_to_compare: ['claude', 'openai', 'grok'],
            requirement_types: ['COMPLIANCE'],
            sample_size_target: 50,
            baseline_model: 'openai',
            source_project: null
          };
        } else if (template === 'cost_focused') {
          quickTestData = {
            name: 'Cost-Focused Optimization Test',
            description: 'Focus on identifying the most cost-effective model with acceptable accuracy',
            models_to_compare: ['claude', 'openai'],
            requirement_types: ['COMPLIANCE'],
            sample_size_target: 25,
            baseline_model: 'openai',
            source_project: null
          };
        }
        
        const response = await api.post('/ab-testing/experiments/', quickTestData);
        
        console.log('Quick test created successfully:', response.data);
        this.toast.success(`Quick A/B test created: ${response.data.name}`);
        await this.loadExperiments();
        
        // Start the experiment immediately
        if (response.data && response.data.id) {
          console.log('Starting experiment:', response.data.id);
          await this.startExperiment(response.data.id);
        }
        
      } catch (error) {
        console.error('Error creating quick test:', error);
        const errorMessage = error.response?.data?.message || error.response?.data?.detail || 'Failed to create quick test';
        this.toast.error(errorMessage);
      }
    },
    
    async createAccuracyFocusedTest() {
      try {
        const accuracyTestData = {
          name: 'Accuracy-Focused A/B Test',
          description: 'A/B test optimized for accuracy over cost, with enhanced human validation',
          models_to_compare: ['claude', 'openai'],
          accuracy_target: 0.90,
          requirements_focus: ['entity_verification', 'policy_limit', 'additional_insured']
        };
        
        const response = await api.post('/ab-testing/experiments/create_accuracy_focused/', accuracyTestData);
        
        console.log('Accuracy-focused test created successfully:', response.data);
        this.toast.success(`Accuracy-focused A/B test created: ${response.data.name}`);
        await this.loadExperiments();
        
        // Start the experiment immediately
        if (response.data && response.data.id) {
          console.log('Starting accuracy-focused experiment:', response.data.id);
          await this.startAccuracyFocusedExperiment(response.data.id);
        }
        
      } catch (error) {
        console.error('Error creating accuracy-focused test:', error);
        const errorMessage = error.response?.data?.message || error.response?.data?.detail || 'Failed to create accuracy-focused test';
        this.toast.error(errorMessage);
      }
    },
    
    async startAccuracyFocusedExperiment(experimentId) {
      try {
        const response = await api.post(`/ab-testing/experiments/${experimentId}/run_accuracy_focused/`, {
          accuracy_threshold: 0.85,
          max_cost_increase: 0.5
        });
        
        console.log('Accuracy-focused experiment started:', response.data);
        this.toast.success('Accuracy-focused experiment started successfully!');
        await this.loadExperiments();
        
      } catch (error) {
        console.error('Error starting accuracy-focused experiment:', error);
        const errorMessage = error.response?.data?.message || error.response?.data?.detail || 'Failed to start accuracy-focused experiment';
        this.toast.error(errorMessage);
      }
    },
    
    async createExperiment() {
      try {
        console.log('Creating experiment with data:', this.newExperiment);
        const response = await api.post('/ab-testing/experiments/', this.newExperiment);
        
        console.log('Experiment created successfully:', response.data);
        this.toast.success(`A/B test experiment created: ${response.data.name}`);
        this.showCreateModal = false;
        this.resetForm();
        await this.loadExperiments();
        
      } catch (error) {
        console.error('Error creating experiment:', error);
        const errorMessage = error.response?.data?.message || error.response?.data?.detail || 'Failed to create experiment';
        this.toast.error(errorMessage);
      }
    },
    
    async startExperiment(experimentId) {
      try {
        console.log('Starting experiment:', experimentId);
        const response = await api.post(`/ab-testing/experiments/${experimentId}/start_experiment/`);
        console.log('Start experiment response:', response.data);
        
        this.toast.success('A/B test started successfully!');
        console.log('Reloading experiments...');
        await this.loadExperiments();
        console.log('Experiments reloaded, new count:', this.experiments.length);
        
        // Start polling now that we have a running experiment
        this.startStatusPolling();
      } catch (error) {
        console.error('Error starting experiment:', error);
        const errorMessage = error.response?.data?.message || error.response?.data?.detail || 'Failed to start experiment';
        this.toast.error(errorMessage);
      }
    },
    
    async pauseExperiment(experimentId) {
      try {
        await api.post(`/ab-testing/experiments/${experimentId}/pause_experiment/`);
        this.toast.success('A/B test paused');
        await this.loadExperiments();
      } catch (error) {
        console.error('Error pausing experiment:', error);
        const errorMessage = error.response?.data?.message || error.response?.data?.detail || 'Failed to pause experiment';
        this.toast.error(errorMessage);
      }
    },
    
    async resumeExperiment(experimentId) {
      try {
        await api.post(`/ab-testing/experiments/${experimentId}/resume_experiment/`);
        this.toast.success('A/B test resumed');
        await this.loadExperiments();
      } catch (error) {
        console.error('Error resuming experiment:', error);
        const errorMessage = error.response?.data?.message || error.response?.data?.detail || 'Failed to resume experiment';
        this.toast.error(errorMessage);
      }
    },
    
    async viewExperimentDetails(experiment) {
      try {
        this.selectedExperiment = experiment;
        
        // Load detailed analytics
        const analyticsResponse = await api.get(`/ab-testing/experiments/${experiment.id}/analytics/`);
        this.experimentAnalytics = analyticsResponse.data;
        
        // Load individual A/B test results for this experiment
        const resultsResponse = await api.get(`/ab-testing/results/?experiment=${experiment.id}`);
        this.experimentResults = resultsResponse.data;
        console.log('Loaded experiment results:', this.experimentResults.length);
        
        // Load existing feedback for all results
        await this.loadExistingFeedback();
        
        // Load insights
        const insightsResponse = await api.get(`/ab-testing/experiments/${experiment.id}/insights/`);
        this.experimentInsights = insightsResponse.data;
        
        // Load accuracy analysis if available
        try {
          const accuracyResponse = await api.get(`/ab-testing/experiments/${experiment.id}/accuracy_analysis/`);
          this.experimentAccuracyAnalysis = accuracyResponse.data;
          console.log('Loaded accuracy analysis:', this.experimentAccuracyAnalysis);
        } catch (accuracyError) {
          console.log('No accuracy analysis available for this experiment');
          this.experimentAccuracyAnalysis = null;
        }
        
        // Load validation status
        try {
          const validationResponse = await api.get(`/ab-testing/experiments/${experiment.id}/validation_status/`);
          this.experimentValidationStatus = validationResponse.data;
          console.log('Loaded validation status:', this.experimentValidationStatus);
        } catch (validationError) {
          console.log('No validation status available for this experiment');
          this.experimentValidationStatus = null;
        }
        
        this.showDetailsModal = true;
        
      } catch (error) {
        console.error('Error loading experiment details:', error);
        this.toast.error('Failed to load experiment details');
      }
    },
    
    async viewInsights(experiment) {
      await this.viewExperimentDetails(experiment);
      // Could scroll to insights section or highlight it
    },
    
    getModelDisplayName(model) {
      const displayNames = {
        // Legacy model names (for backward compatibility)
        'openai': 'GPT-4 Turbo',
        'claude': 'Claude 3.5 Sonnet', 
        'grok': 'Grok 2',
        'gemini': 'Gemini 1.5 Pro',
        
        // Specific model names
        'gpt-4-turbo': 'GPT-4 Turbo',
        'gpt-4o': 'GPT-4o',
        'gpt-3.5-turbo': 'GPT-3.5 Turbo',
        'claude-3.5-sonnet': 'Claude 3.5 Sonnet',
        'claude-3-sonnet': 'Claude 3 Sonnet',
        'claude-3-haiku': 'Claude 3 Haiku',
        'grok-2-1212': 'Grok 2',
        'gemini-1.5-pro': 'Gemini 1.5 Pro',
        'gemini-1.5-flash': 'Gemini Flash Latest',
        'gemini-2.5-pro': 'Gemini 2.5 Pro',
        'gemini-2.5-flash': 'Gemini 2.5 Flash',
        'gemini-pro': 'Gemini Pro'
      };
      return displayNames[model] || model;
    },
    
    getModelIcon(model) {
      const icons = {
        'openai': '🤖',
        'claude': '🧠',
        'grok': '⚡',
        'gemini': '💎',
        'gpt-4-turbo': '🤖',
        'gpt-4o': '🤖',
        'gpt-3.5-turbo': '🤖',
        'claude-3.5-sonnet': '🧠',
        'claude-3-sonnet': '🧠',
        'claude-3-haiku': '🧠',
        'grok-2-1212': '⚡',
        'gemini-1.5-pro': '💎',
        'gemini-1.5-flash': '💎',
        'gemini-2.5-pro': '💎',
        'gemini-2.5-flash': '💎',
        'gemini-pro': '💎'
      };
      return icons[model] || '🔮';
    },
    
    getResultIcon(result) {
      const icons = {
        'PASS': 'fas fa-check-circle',
        'FAIL': 'fas fa-times-circle',
        'UNCLEAR': 'fas fa-question-circle'
      };
      return icons[result] || 'fas fa-question-circle';
    },
    
    getConfidenceClass(confidence) {
      if (confidence >= 0.8) return 'confidence-high';
      if (confidence >= 0.6) return 'confidence-medium';
      return 'confidence-low';
    },
    
    getFilteredResults() {
      if (this.resultsFilter === 'all') {
        return this.experimentResults;
      } else if (this.resultsFilter === 'disagreements') {
        return this.experimentResults.filter(result => !this.checkModelsAgree(result.model_results));
      } else if (this.resultsFilter === 'needsValidation') {
        return this.experimentResults.filter(result => !result.human_validation);
      }
      return this.experimentResults;
    },
    
    getDisagreementCount() {
      return this.experimentResults.filter(result => !this.checkModelsAgree(result.model_results)).length;
    },
    
    getNeedsValidationCount() {
      return this.experimentResults.filter(result => !result.human_validation).length;
    },
    
    toggleReasoning(resultId, model) {
      const key = `${resultId}_${model}`;
      this.expandedReasoning[key] = !this.expandedReasoning[key];
    },
    
    toggleReasoningSummary(resultId, model) {
      const key = `${resultId}_${model}`;
      this.expandedReasoningSummary[key] = !this.expandedReasoningSummary[key];
    },
    
    isReasoningSummaryExpanded(resultId, model) {
      const key = `${resultId}_${model}`;
      return this.expandedReasoningSummary[key] || false;
    },
    
    isReasoningExpanded(resultId, model) {
      const key = `${resultId}_${model}`;
      return this.expandedReasoning[key] || false;
    },
    
    getReasoningToggleText(resultId, model) {
      return this.isReasoningExpanded(resultId, model) ? 'Hide Reasoning' : 'Show Reasoning';
    },
    
    formatModelResult(result) {
      // Normalize the result first
      const normalizedResult = this.normalizeResult(result);
      
      const resultMap = {
        'PASS': '✅ REQUIREMENT MET',
        'FAIL': '❌ REQUIREMENT NOT MET', 
        'UNCLEAR': '❓ UNABLE TO DETERMINE',
        'ERROR': '⚠️ PROCESSING ERROR'
      };
      return resultMap[normalizedResult] || result || 'NO RESULT';
    },
    
    normalizeResult(result) {
      if (!result) return 'UNCLEAR';
      
      const upperResult = result.toString().toUpperCase();
      
      // Normalize common variations
      if (['PASS', 'MET', 'PASSED', 'MEETS', 'YES'].includes(upperResult)) {
        return 'PASS';
      } else if (['FAIL', 'FAILED', 'NOT_MET', 'DOES_NOT_MEET', 'NO'].includes(upperResult)) {
        return 'FAIL';
      } else if (['UNCLEAR', 'UNKNOWN', 'INCONCLUSIVE', 'MAYBE'].includes(upperResult)) {
        return 'UNCLEAR';
      }
      
      return upperResult;
    },
    
    getModelResultsDebug(modelResults) {
      // Show raw results for debugging disagreements
      const results = Object.entries(modelResults).map(([model, data]) => 
        `${model}:${data.result || 'null'}`
      ).join(', ');
      return results;
    },
    
    checkModelsAgree(modelResults) {
      // Client-side check to verify agreement logic
      const results = Object.values(modelResults)
        .map(data => this.normalizeResult(data.result))
        .filter(result => result !== 'ERROR');
      
      const uniqueResults = new Set(results);
      return uniqueResults.size <= 1;
    },
    
    getReasoningSummary(reasoning) {
      if (!reasoning) return 'No reasoning provided';
      
      // Extract the first sentence or first 150 characters as summary
      const sentences = reasoning.split(/[.!?]+/);
      const firstSentence = sentences[0]?.trim();
      
      if (firstSentence && firstSentence.length > 20) {
        return firstSentence.length > 150 
          ? firstSentence.substring(0, 150) + '...'
          : firstSentence;
      }
      
      // Fallback to character limit
      return reasoning.length > 150 
        ? reasoning.substring(0, 150) + '...'
        : reasoning;
    },

    truncateReasoning(reasoning, maxLength = 100) {
      if (!reasoning) return '';
      return reasoning.length > maxLength 
        ? reasoning.substring(0, maxLength) + '...'
        : reasoning;
    },

    hasModelFeedback(result, model) {
      const key = `${result.id}_${model}`;
      return this.modelValidations[key] !== undefined;
    },

    // Main results reasoning expansion methods
    toggleMainReasoning(resultId, model) {
      const key = `main_${resultId}_${model}`;
      this.expandedReasoning[key] = !this.expandedReasoning[key];
    },

    isMainReasoningExpanded(resultId, model) {
      const key = `main_${resultId}_${model}`;
      return this.expandedReasoning[key] || false;
    },
    
    async validateModelResult(result, model, validation) {
      const loadingKey = `${result.id}_${model}`;
      
      try {
        // Set loading state
        this.validatingModels[loadingKey] = true;
        
        // Store model-specific validation locally (Vue 3 compatible)
        const key = `${result.id}_${model}`;
        this.modelValidations[key] = validation;
        
        // Handle PARTIAL and WRONG validation with detailed feedback modal
        if (validation === 'PARTIAL' || validation === 'WRONG') {
          this.openCorrectionModal(result, model);
          return;
        }
        
        // Send detailed feedback about this specific model's performance
        const feedbackData = {
          feedback_type: 'ACCURACY',
          model_used: model,
          accuracy_rating: validation === 'CORRECT' ? 'PERFECT' : 'WRONG',
          reasoning_quality: validation === 'CORRECT' ? 'EXCELLENT' : 'FLAWED',
          specific_issues: validation === 'WRONG' ? 
            `${this.getModelDisplayName(model)} incorrectly concluded ${result.model_results[model].result}` :
            `${this.getModelDisplayName(model)} correctly concluded ${result.model_results[model].result}`,
          correct_answer: validation === 'CORRECT' ? result.model_results[model].result : 'Opposite of what model concluded',
          issue_categories: validation === 'WRONG' ? this.inferIssueCategories(result, model) : []
        };
        
        console.log('Submitting simple feedback:', feedbackData);
        const response = await api.post(`/ab-testing/results/${result.id}/add_detailed_feedback/`, feedbackData);
        console.log('Simple feedback response:', response.data);
        
        // Force reload of feedback to ensure persistence
        await this.loadExistingFeedback();
        
        this.toast.success(`${this.getModelDisplayName(model)} marked as ${validation.toLowerCase()}`);
        
        // Model-specific validation complete - no overall validation needed
        // Users validate each model individually for better feedback granularity
        
      } catch (error) {
        console.error('Error validating model result:', error);
        this.toast.error(`Failed to validate ${this.getModelDisplayName(model)} result`);
      } finally {
        // Clear loading state
        this.validatingModels[loadingKey] = false;
      }
    },
    
    isValidating(resultId, model) {
      const key = `${resultId}_${model}`;
      return this.validatingModels[key] || false;
    },
    
    async openCorrectionModal(result, model) {
      // Get the current validation type to set appropriate defaults
      const currentValidation = this.getModelValidation(result, model);
      
      // Initialize with empty data first
      this.correctionData = {
        result: result,
        model: model,
        correctReasoning: '',
        correctEvidence: '',
        issueCategories: [],
        specificIssues: '',
        correctAnswer: '',
        accuracyRating: currentValidation === 'PARTIAL' ? 'ACCEPTABLE' : 'POOR', // Default based on validation type
        reasoningQuality: currentValidation === 'PARTIAL' ? 'UNCLEAR' : 'FLAWED',
        evidenceQuality: currentValidation === 'PARTIAL' ? 'PARTIAL' : 'WEAK',
        suggestedImprovement: '',
        existingFeedbackId: null,
        isEditingExisting: false
      };
      
      this.showCorrectionModal = true;
      
      // Try to load existing feedback
      try {
        const response = await api.get(`/ab-testing/results/${result.id}/get_feedback/?model=${model}`);
        
        if (response.data.has_feedback) {
          const feedback = response.data.feedback;
          
          // Pre-populate the form with existing feedback
          this.correctionData = {
            ...this.correctionData,
            correctReasoning: feedback.correct_reasoning || '',
            correctEvidence: feedback.correct_answer || '', // Note: using correct_answer for evidence field
            correctAnswer: feedback.correct_answer || '',
            issueCategories: feedback.issue_categories || [],
            specificIssues: feedback.specific_issues || '',
            accuracyRating: feedback.accuracy_rating || '',
            reasoningQuality: feedback.reasoning_quality || '',
            evidenceQuality: feedback.evidence_quality || '',
            suggestedImprovement: feedback.suggested_improvement || '',
            existingFeedbackId: feedback.id,
            isEditingExisting: true
          };
          
          console.log('Loaded existing feedback:', feedback);
        }
      } catch (error) {
        console.warn('Could not load existing feedback:', error);
        // Continue with empty form - this is not a critical error
      }
    },
    
    closeCorrectionModal() {
      this.showCorrectionModal = false;
      this.correctionData = {
        result: null,
        model: null,
        correctReasoning: '',
        correctEvidence: '',
        issueCategories: [],
        specificIssues: '',
        correctAnswer: '',
        accuracyRating: '',
        reasoningQuality: '',
        evidenceQuality: '',
        suggestedImprovement: '',
        existingFeedbackId: null,
        isEditingExisting: false
      };
    },
    
    async submitCorrection() {
      try {
        // Ensure accuracy rating reflects "Right but Needs Correction" if not explicitly set
        const accuracyRating = this.correctionData.accuracyRating || 'ACCEPTABLE';
        
        const feedbackData = {
          feedback_type: 'ACCURACY',
          model_used: this.correctionData.model,
          accuracy_rating: accuracyRating,
          reasoning_quality: this.correctionData.reasoningQuality || 'UNCLEAR',
          evidence_quality: this.correctionData.evidenceQuality || 'PARTIAL',
          issue_categories: this.correctionData.issueCategories,
          specific_issues: this.correctionData.specificIssues,
          correct_reasoning: this.correctionData.correctReasoning,
          correct_answer: this.correctionData.correctAnswer || this.correctionData.result.model_results[this.correctionData.model].result,
          suggested_improvement: this.correctionData.suggestedImprovement || `Improve reasoning quality: ${this.correctionData.correctReasoning}`
        };
        
        if (this.correctionData.correctEvidence) {
          feedbackData.suggested_improvement += ` Evidence should be: ${this.correctionData.correctEvidence}`;
        }
        
        console.log('Submitting feedback data:', feedbackData);
        const response = await api.post(`/ab-testing/results/${this.correctionData.result.id}/add_detailed_feedback/`, feedbackData);
        console.log('Feedback submission response:', response.data);
        
        // Mark validation locally based on the accuracy rating
        const key = `${this.correctionData.result.id}_${this.correctionData.model}`;
        if (accuracyRating === 'ACCEPTABLE') {
          this.modelValidations[key] = 'PARTIAL';  // "Right but Needs Correction"
        } else if (accuracyRating === 'POOR' || accuracyRating === 'WRONG') {
          this.modelValidations[key] = 'WRONG';    // "Wrong"
        }
        
        console.log(`Set local validation for ${key}: ${this.modelValidations[key]}`);
        
        // Force reload of feedback to ensure persistence
        await this.loadExistingFeedback();
        
        this.toast.success(`Correction submitted for ${this.getModelDisplayName(this.correctionData.model)}`);
        this.closeCorrectionModal();
        
      } catch (error) {
        console.error('Error submitting correction:', error);
        this.toast.error('Failed to submit correction');
      }
    },
    
    toggleDocumentViewer(resultId) {
      this.expandedDocumentViewers[resultId] = !this.expandedDocumentViewers[resultId];
    },

    // Check if feedback exists for a result/model combination
    async checkFeedbackExists(result, model) {
      try {
        const response = await api.get(`/ab-testing/results/${result.id}/get_feedback/?model=${model}`);
        return response.data.has_feedback;
      } catch (error) {
        console.warn('Could not check feedback existence:', error);
        return false;
      }
    },

    // Get feedback indicator class for styling
    getFeedbackIndicatorClass(result, model) {
      const key = `${result.id}_${model}`;
      if (this.modelValidations[key]) {
        return this.modelValidations[key].toLowerCase();
      }
      return '';
    },

    // Load existing feedback for all experiment results
    async loadExistingFeedback() {
      if (!this.experimentResults || this.experimentResults.length === 0) {
        return;
      }

      console.log('Loading existing feedback for all results...');
      
      for (const result of this.experimentResults) {
        for (const model of Object.keys(result.model_results)) {
          try {
            const response = await api.get(`/ab-testing/results/${result.id}/get_feedback/?model=${model}`);
            
            if (response.data.has_feedback) {
              const feedback = response.data.feedback;
              
              // Set validation state based on feedback
              const key = `${result.id}_${model}`;
              
              // Determine validation type based on accuracy rating
              let validationType = null;
              if (feedback.accuracy_rating === 'PERFECT' || feedback.accuracy_rating === 'GOOD') {
                validationType = 'CORRECT';
              } else if (feedback.accuracy_rating === 'ACCEPTABLE') {
                validationType = 'PARTIAL';
              } else if (feedback.accuracy_rating === 'POOR') {
                // POOR could be either PARTIAL or WRONG - check if correction details exist
                if (feedback.correct_reasoning || feedback.correct_answer) {
                  validationType = 'PARTIAL'; // Has corrections = "Right but Needs Correction"
                } else {
                  validationType = 'WRONG';   // No corrections = "Wrong"
                }
              } else if (feedback.accuracy_rating === 'WRONG') {
                validationType = 'WRONG';
              }
              
              if (validationType) {
                // Set validation state for Vue 3 reactivity
                this.modelValidations[key] = validationType;
                console.log(`Set validation for ${key}: ${validationType}`);
              }
              
              console.log(`Loaded feedback for result ${result.id}, model ${model}: ${feedback.accuracy_rating}`);
            }
          } catch (error) {
            // Silently continue if feedback can't be loaded for this result/model
            console.warn(`Could not load feedback for result ${result.id}, model ${model}:`, error);
          }
        }
      }
      
      console.log('Finished loading existing feedback. Model validations:', this.modelValidations);
      
      // Force reactivity update to ensure buttons reflect the loaded state
      this.$forceUpdate();
    },
    
    isDocumentViewerExpanded(resultId) {
      return this.expandedDocumentViewers[resultId] || false;
    },
    
    getDocumentUrlForResult(result) {
      // Check if we already have the document details cached
      if (result.documentDetails && result.documentDetails.file_url) {
        return this.getDocumentUrl(result.documentDetails);
      }
      
      // If we have document_url directly, use it
      if (result.document_url) {
        return this.getDocumentUrl({ file_url: result.document_url, name: result.document_name });
      }
      
      // If we only have document_id, fetch document details
      if (result.document_id && !result.fetchingDocument) {
        this.fetchDocumentForResult(result);
      }
      
      return null;
    },
    
    async fetchDocumentForResult(result) {
      if (result.fetchingDocument) return;
      
      result.fetchingDocument = true;
      
      try {
        const docResponse = await api.get(`/documents/${result.document_id}/`);
        const document = docResponse.data;
        
        // Store document details on the result object
        result.documentDetails = document;
        result.fetchingDocument = false;
        
        console.log(`Fetched document details for result ${result.id}:`, document);
        
        // Force reactivity update
        this.$forceUpdate();
        
      } catch (error) {
        console.error('Error fetching document details:', error);
        result.fetchingDocument = false;
        result.documentDetails = null;
      }
    },
    
    
    getDocumentUrl(document) {
      // Use unified document URL helper
      const { getDocumentUrl } = require('@/utils/documentUrlHelper');
      return getDocumentUrl(document) || '';
    },
    
    isPDF(document) {
      if (!document) return false;
      return document.name && document.name.toLowerCase().endsWith('.pdf');
    },
    
    onDocumentLoad(resultId) {
      console.log(`Document loaded successfully for result ${resultId}`);
    },
    
    onDocumentError(resultId) {
      console.error(`Error loading document for result ${resultId}`);
    },
    
    getModelValidation(result, model) {
      const key = `${result.id}_${model}`;
      const validation = this.modelValidations[key] || null;
      
      // Debug logging for button state issues
      if (result.id === 50 && model === 'claude') {
        console.log(`getModelValidation(${key}): ${validation}, available keys:`, Object.keys(this.modelValidations));
      }
      
      return validation;
    },
    
    inferIssueCategories(result, model) {
      const categories = [];
      const modelResult = result.model_results[model];
      
      // Infer issue categories based on the model and result type
      if (model === 'openai' && modelResult.result === 'UNCLEAR') {
        categories.push('OVERTHINKING');
      }
      
      if (result.requirement_type.includes('entity') || result.requirement_text.toLowerCase().includes('entity')) {
        categories.push('ENTITY_CONFUSION');
      }
      
      if (result.requirement_text.toLowerCase().includes('policy') || result.requirement_text.toLowerCase().includes('coverage')) {
        categories.push('POLICY_MISREAD');
      }
      
      return categories;
    },
    
    // validateResult function removed - using model-specific validations only
    
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString();
    },
    
    async deleteExperiment(experimentId) {
      if (confirm('Are you sure you want to delete this experiment? This action cannot be undone.')) {
        try {
          await api.delete(`/ab-testing/experiments/${experimentId}/`);
          this.toast.success('Experiment deleted successfully');
          await this.loadExperiments();
        } catch (error) {
          console.error('Error deleting experiment:', error);
          const errorMessage = error.response?.data?.message || error.response?.data?.detail || 'Failed to delete experiment';
          this.toast.error(errorMessage);
        }
      }
    },
    
    async bulkDeleteDraftExperiments() {
      if (confirm(`Are you sure you want to delete all ${this.draftExperiments.length} draft experiments? This will clean up all test experiments created during setup.`)) {
        try {
          const experimentIds = this.draftExperiments.map(exp => exp.id);
          
          await api.post('/ab-testing/experiments/bulk_delete/', {
            experiment_ids: experimentIds
          });
          
          this.toast.success(`Successfully deleted ${this.draftExperiments.length} test experiments`);
          await this.loadExperiments();
        } catch (error) {
          console.error('Error bulk deleting experiments:', error);
          const errorMessage = error.response?.data?.message || error.response?.data?.detail || 'Failed to delete experiments';
          this.toast.error(errorMessage);
        }
      }
    },
    
    async loadAllResults(page = 1, pageSize = 50) {
      try {
        const response = await api.get(`/ab-testing/results/?page=${page}&page_size=${pageSize}`);
        
        // Handle both paginated and non-paginated responses
        if (response.data.results) {
          // Paginated response
          this.allResults = response.data.results;
          this.resultsCount = response.data.count || response.data.results.length;
          this.hasMoreResults = response.data.next !== null;
          this.currentResultsPage = page;
          console.log(`Loaded ${this.allResults.length} A/B test results (page ${page}, total: ${this.resultsCount})`);
        } else {
          // Non-paginated response (fallback)
          this.allResults = response.data;
          this.resultsCount = response.data.length;
          this.hasMoreResults = false;
          this.currentResultsPage = 1;
          console.log('Loaded A/B test results (non-paginated):', this.allResults.length);
        }
      } catch (error) {
        console.error('Error loading A/B test results:', error);
        // Don't show error toast for this - it's not critical
      }
    },

    async loadNextPage() {
      if (this.hasMoreResults) {
        await this.loadAllResults(this.currentResultsPage + 1, this.resultsPageSize);
      }
    },

    async loadPreviousPage() {
      if (this.currentResultsPage > 1) {
        await this.loadAllResults(this.currentResultsPage - 1, this.resultsPageSize);
      }
    },

    // Requirement-focused view methods
    loadRequirementResults() {
      if (!this.selectedRequirementId) {
        this.requirementResults = [];
        return;
      }
      
      this.requirementResults = this.allResults.filter(
        result => result.requirement_id === this.selectedRequirementId
      );
      
      console.log(`Loaded ${this.requirementResults.length} results for requirement ${this.selectedRequirementId}`);
    },

    async markAllCorrect() {
      if (!this.selectedRequirementId) return;
      
      try {
        for (const result of this.requirementResults) {
          for (const model of Object.keys(result.model_results)) {
            await this.validateModelResult(result, model, 'CORRECT');
          }
        }
        this.toast.success(`Marked all results as correct for this requirement`);
      } catch (error) {
        console.error('Error marking all correct:', error);
        this.toast.error('Failed to mark all as correct');
      }
    },

    async markAllWrong() {
      if (!this.selectedRequirementId) return;
      
      try {
        for (const result of this.requirementResults) {
          for (const model of Object.keys(result.model_results)) {
            await this.validateModelResult(result, model, 'WRONG');
          }
        }
        this.toast.success(`Marked all results as wrong for this requirement`);
      } catch (error) {
        console.error('Error marking all wrong:', error);
        this.toast.error('Failed to mark all as wrong');
      }
    },
    
    getBestModel(modelResults) {
      if (!modelResults || Object.keys(modelResults).length === 0) return 'N/A';
      
      // Find the model with lowest cost and highest confidence
      let bestModel = null;
      let bestScore = -1;
      
      for (const [model, result] of Object.entries(modelResults)) {
        if (result.result === 'ERROR') continue;
        
        // Score = confidence / cost (higher is better)
        const score = (result.confidence || 0.5) / (result.cost || 0.01);
        if (score > bestScore) {
          bestScore = score;
          bestModel = model;
        }
      }
      
      return bestModel ? this.getModelDisplayName(bestModel) : 'N/A';
    },
    
    getCostSavings(modelResults) {
      if (!modelResults || Object.keys(modelResults).length < 2) return 'N/A';
      
      const costs = Object.values(modelResults)
        .filter(r => r.result !== 'ERROR' && r.cost)
        .map(r => r.cost);
      
      if (costs.length < 2) return 'N/A';
      
      const maxCost = Math.max(...costs);
      const minCost = Math.min(...costs);
      const savings = ((maxCost - minCost) / maxCost * 100).toFixed(1);
      
      return `${savings}%`;
    },
    
    getSpeedImprovement(modelResults) {
      if (!modelResults || Object.keys(modelResults).length < 2) return 'N/A';
      
      const times = Object.values(modelResults)
        .filter(r => r.result !== 'ERROR' && r.processing_time)
        .map(r => r.processing_time);
      
      if (times.length < 2) return 'N/A';
      
      const maxTime = Math.max(...times);
      const minTime = Math.min(...times);
      const improvement = ((maxTime - minTime) / maxTime * 100).toFixed(1);
      
      return `${improvement}%`;
    },
    
    getWinnerForResult(modelResults) {
      if (!modelResults || Object.keys(modelResults).length === 0) return 'N/A';
      
      // For agent responses, prioritize: 1) Under 1 min, 2) Low cost, 3) High confidence
      let bestModel = null;
      let bestScore = -1;
      
      for (const [model, result] of Object.entries(modelResults)) {
        if (result.result === 'ERROR') continue;
        
        const time = result.processing_time || 0;
        const cost = result.cost || 0.01;
        const confidence = result.confidence || 0.5;
        
        // Heavy penalty for over 60 seconds (agent response requirement)
        const timePenalty = time > 60 ? 0.1 : 1.0;
        
        // Score = (confidence / cost) * time_penalty
        const score = (confidence / cost) * timePenalty;
        
        if (score > bestScore) {
          bestScore = score;
          bestModel = model;
        }
      }
      
      return bestModel ? this.getModelDisplayName(bestModel) : 'N/A';
    },
    
    getWinnerReason(modelResults) {
      if (!modelResults || Object.keys(modelResults).length < 2) return '';
      
      const models = Object.keys(modelResults).filter(m => modelResults[m].result !== 'ERROR');
      if (models.length < 2) return '';
      
      const costs = models.map(m => modelResults[m].cost || 0);
      const times = models.map(m => modelResults[m].processing_time || 0);
      
      const costSavings = ((Math.max(...costs) - Math.min(...costs)) / Math.max(...costs) * 100).toFixed(0);
      const timeImprovement = ((Math.max(...times) - Math.min(...times)) / Math.max(...times) * 100).toFixed(0);
      
      const reasons = [];
      if (parseInt(costSavings) > 10) reasons.push(`${costSavings}% cheaper`);
      if (parseInt(timeImprovement) > 10) reasons.push(`${timeImprovement}% faster`);
      if (Math.max(...times) > 60) reasons.push('under 1-min limit');
      
      return reasons.join(', ') || 'similar performance';
    },
    
    startStatusPolling() {
      // Clear any existing interval first to prevent duplicates
      if (this.statusPollingInterval) {
        clearInterval(this.statusPollingInterval);
        this.statusPollingInterval = null;
      }
      
      // Only start polling if there are running or paused experiments
      const activeExperiments = this.experiments.filter(exp => 
        exp.status === 'RUNNING' || exp.status === 'PAUSED'
      );
      
      if (activeExperiments.length === 0) {
        console.log('No active experiments - polling stopped');
        return; // Don't start polling if nothing is running
      }
      
      console.log(`Starting polling for ${activeExperiments.length} active experiments`);
      
      // Track polling attempts to prevent infinite polling on stuck experiments
      let pollAttempts = 0;
      const MAX_POLL_ATTEMPTS = 360; // 1 hour at 10-second intervals
      
      // Poll every 10 seconds for active experiments
      this.statusPollingInterval = setInterval(async () => {
        pollAttempts++;
        
        // Safety check: Stop polling after 1 hour to prevent infinite loops
        if (pollAttempts >= MAX_POLL_ATTEMPTS) {
          console.warn('Polling timeout reached (1 hour) - stopping automatic polling');
          console.warn('Experiments may be stuck. Please check backend logs or refresh the page.');
          clearInterval(this.statusPollingInterval);
          this.statusPollingInterval = null;
          this.toast.warning('Polling stopped after 1 hour. Refresh page to resume.');
          return;
        }
        
        const activeExperiments = this.experiments.filter(exp => 
          exp.status === 'RUNNING' || exp.status === 'PAUSED'
        );
        
        if (activeExperiments.length > 0) {
          console.log(`Polling status for ${activeExperiments.length} active experiments (attempt ${pollAttempts}/${MAX_POLL_ATTEMPTS})`);
          await this.loadExperiments(); // Refresh experiment data
        } else {
          // No more active experiments - stop polling
          console.log('All experiments completed - stopping polling');
          clearInterval(this.statusPollingInterval);
          this.statusPollingInterval = null;
        }
      }, 10000); // Poll every 10 seconds
    },
    
    getTimeElapsed(startTime) {
      if (!startTime) return 'Unknown';
      
      const start = new Date(startTime);
      const now = new Date();
      const diffMs = now - start;
      
      const minutes = Math.floor(diffMs / 60000);
      const seconds = Math.floor((diffMs % 60000) / 1000);
      
      if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
      } else {
        return `${seconds}s`;
      }
    },
    
    getProgressStatus(experiment) {
      if (!experiment.started_at) return 'Not started';
      
      const progress = experiment.progress_percentage || 0;
      
      if (progress === 0) return 'Initializing...';
      if (progress < 25) return 'Starting analysis...';
      if (progress < 50) return 'Processing documents...';
      if (progress < 75) return 'Running comparisons...';
      if (progress < 100) return 'Finalizing results...';
      return 'Completed';
    },
    
    resetForm() {
      this.newExperiment = {
        name: '',
        description: '',
        models_to_compare: ['openai'],
        baseline_model: 'openai',
        sample_size_target: 50,
        accuracy_threshold: 0.95,
        max_cost_increase: 0.10,
        requirement_types: [],
        source_project: null,
        comprehensive_checks: [],
        project: null
      };
    }
  }
};
</script>

<style scoped>
.ab-testing-dashboard {
  /* Page chrome: global .entity-list-page (left-anchored spoke) */
  padding-bottom: 2.5rem;
}

.purpose-panel {
  margin-bottom: 1.5rem;
  padding: 1.15rem 1.25rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}

.purpose-panel h2 {
  margin: 0 0 0.5rem;
  font-size: 1.05rem;
  font-weight: 650;
  color: #0f172a;
}

.purpose-steps {
  margin: 0 0 1rem;
  padding-left: 1.25rem;
  color: #334155;
  font-size: 0.925rem;
  line-height: 1.45;
}

.purpose-steps li + li {
  margin-top: 0.25rem;
}

.quick-start-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.btn-danger-text {
  color: #b91c1c;
}

.btn-danger-text:hover {
  border-color: #fecaca;
  background: #fef2f2;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.status-text {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #64748b;
}

.results-wrap {
  margin-top: 1.5rem;
}

.results-section {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}

.results-table-container {
  overflow-x: auto;
}

.result-row:hover {
  background-color: #f8f9fa;
}

.models-tested {
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
}

.model-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.model-openai {
  background-color: #e3f2fd;
  color: #1976d2;
}

.model-claude {
  background-color: #f3e5f5;
  color: #7b1fa2;
}

.model-grok {
  background-color: #e8f5e8;
  color: #388e3c;
}

.best-model-badge {
  background-color: #4caf50;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.cost-savings {
  color: #4caf50;
  font-weight: 500;
}

.speed-improvement {
  color: #2196f3;
  font-weight: 500;
}

.requirement-info {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Detailed Results Styling */
.detailed-results-section {
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #dee2e6;
}

.detailed-results-grid {
  display: grid;
  gap: 1.5rem;
  margin-top: 1rem;
}

.detailed-result-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  border-left: 4px solid #007bff;
}

.result-header h6 {
  margin: 0;
  color: #495057;
  font-weight: 600;
}

.document-name {
  color: #6c757d;
  font-size: 0.875rem;
}

.requirement-details {
  margin: 1rem 0;
  padding: 0.75rem;
  background: white;
  border-radius: 4px;
  border-left: 3px solid #28a745;
}

.model-comparison-detailed {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
  margin: 1rem 0;
}

.model-result-detailed {
  background: white;
  border-radius: 6px;
  padding: 1rem;
  border: 1px solid #dee2e6;
}

.model-openai {
  border-left: 4px solid #1976d2;
}

.model-claude {
  border-left: 4px solid #7b1fa2;
}

.model-grok {
  border-left: 4px solid #388e3c;
}

.model-header-detailed {
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.model-name {
  font-weight: 600;
  font-size: 1rem;
}

.result-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.result-met {
  background-color: #d4edda;
  color: #155724;
}

.result-not_met {
  background-color: #f8d7da;
  color: #721c24;
}

.result-unclear {
  background-color: #fff3cd;
  color: #856404;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.metric-item {
  display: flex;
  justify-content: space-between;
  padding: 0.25rem 0;
}

.metric-item label {
  font-weight: 500;
  color: #6c757d;
}

.metric-item .value {
  font-weight: 600;
}

.time-critical .value {
  color: #dc3545;
}

.time-warning {
  color: #dc3545;
  font-size: 0.75rem;
}

.time-good {
  color: #28a745;
  font-size: 0.75rem;
}

.reasoning-section {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #dee2e6;
}

.reasoning-section label {
  font-weight: 600;
  color: #495057;
  display: block;
  margin-bottom: 0.5rem;
}

.reasoning-text {
  background: #f8f9fa;
  padding: 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;
  line-height: 1.4;
  max-height: 100px;
  overflow-y: auto;
}

.test-winner {
  margin-top: 1rem;
  text-align: center;
}

.winner-badge {
  background: linear-gradient(135deg, #28a745, #20c997);
  color: white;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  display: inline-block;
}

.winner-badge strong {
  display: block;
  font-size: 1rem;
}

.winner-badge small {
  display: block;
  opacity: 0.9;
  margin-top: 0.25rem;
}

.experiments-section {
  padding: 0;
}

.experiments-group {
  margin-bottom: 2rem;
}

.experiments-group h3 {
  color: #0f172a;
  font-size: 1.15rem;
  font-weight: 650;
  margin: 0 0 1rem;
}

.experiments-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 360px), 1fr));
  gap: 1rem;
}

.experiment-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1.25rem;
  transition: border-color 0.15s ease;
}

.experiment-card:hover {
  border-color: #cbd5e1;
}

.experiment-card.active {
  border-left: 3px solid #16a34a;
}

.experiment-card.completed {
  border-left: 3px solid var(--p-accent, #1e3a5f);
}

.experiment-card.draft {
  border-left: 3px solid #94a3b8;
}

.experiment-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.experiment-header h4 {
  margin: 0;
  color: #2c3e50;
  font-weight: 600;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.running {
  background: #d4edda;
  color: #155724;
}

.status-badge.paused {
  background: #fff3cd;
  color: #856404;
}

.status-badge.completed {
  background: #e2e3f1;
  color: #6f42c1;
}

.progress-section {
  margin: 1rem 0;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #28a745, #20c997);
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 0.875rem;
  color: #6c757d;
  margin-top: 0.25rem;
  display: block;
}

.models-being-tested, .models-tested {
  margin: 1rem 0;
}

.model-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  margin: 0.25rem 0.25rem 0.25rem 0;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
}

.model-badge.openai {
  background: #e3f2fd;
  color: #1976d2;
}

.model-badge.claude {
  background: #f3e5f5;
  color: #7b1fa2;
}

.model-badge.grok {
  background: #fff3e0;
  color: #f57c00;
}

.experiment-stats {
  display: flex;
  gap: 1rem;
  margin: 1rem 0;
}

.stat {
  text-align: center;
}

.stat-value {
  display: block;
  font-size: 1.25rem;
  font-weight: 600;
  color: #2c3e50;
}

.stat-label {
  font-size: 0.75rem;
  color: #6c757d;
  text-transform: uppercase;
}

.experiment-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #dee2e6;
}

.empty-state {
  text-align: center;
  padding: 3rem 1.5rem;
  color: #475569;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  background: #f8fafc;
}

.empty-icon {
  font-size: 2.5rem;
  margin-bottom: 0.75rem;
  color: #94a3b8;
}

.loading-state {
  text-align: center;
  padding: 3rem 1.5rem;
  color: #475569;
  font-size: 1rem;
}

.loading-state i {
  font-size: 1.5rem;
  margin-bottom: 0.75rem;
  color: var(--p-accent, #1e3a5f);
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-container {
  background: white;
  border-radius: 12px;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-container.modal-large {
  max-width: 800px;
}

.modal-container.modal-xl {
  max-width: 1200px;
}

.modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  color: #2c3e50;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6c757d;
  cursor: pointer;
}

.modal-body {
  padding: 1.5rem;
}

.experiment-form .form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #2c3e50;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 1rem;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.model-selection {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.model-category {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1rem;
  background: #fafafa;
}

.model-category h6 {
  margin: 0 0 0.75rem 0;
  color: #2c3e50;
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  padding: 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  transition: all 0.2s;
}

.checkbox-label:hover {
  background: #f8f9fa;
  border-color: #667eea;
}

.checkbox-label input[type="checkbox"] {
  margin: 0;
}

/* Model provider checkmark styles */
.checkmark {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  display: inline-block;
}

.checkmark.openai {
  background: linear-gradient(135deg, #10a37f, #1a7f64);
}

.checkmark.claude {
  background: linear-gradient(135deg, #ff6b35, #f7931e);
}

.checkmark.grok {
  background: linear-gradient(135deg, #ff6b6b, #ee5a24);
}

.checkmark.gemini {
  background: linear-gradient(135deg, #4285f4, #34a853);
}

.checkmark.compliance {
  background: linear-gradient(135deg, #667eea, #764ba2);
}

.checkmark.cert {
  background: linear-gradient(135deg, #f093fb, #f5576c);
}

.checkmark.risk {
  background: linear-gradient(135deg, #ffecd2, #fcb69f);
}

.checkmark.check {
  background: linear-gradient(135deg, #a8edea, #fed6e3);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #dee2e6;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #5a6fd8;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-outline-primary {
  border: 1px solid #667eea;
  color: #667eea;
  background: transparent;
}

.btn-outline-primary:hover {
  background: #667eea;
  color: white;
}

.btn-outline-success {
  border: 1px solid #28a745;
  color: #28a745;
  background: transparent;
}

.btn-outline-info {
  border: 1px solid #17a2b8;
  color: #17a2b8;
  background: transparent;
}

.btn-sm {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Experiment Details Styles */
.experiment-details {
  display: grid;
  gap: 2rem;
}

.progress-overview {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2rem;
  align-items: start;
}

.progress-card {
  text-align: center;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.progress-circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: conic-gradient(#28a745 0deg, #e9ecef 0deg);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 1rem auto;
  position: relative;
}

.progress-percentage {
  font-weight: 700;
  font-size: 1.1rem;
  color: #2c3e50;
}

.model-performance-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.model-performance-card {
  padding: 1rem;
  border-radius: 8px;
  border: 2px solid #dee2e6;
}

.model-performance-card.openai {
  border-color: #1976d2;
  background: #e3f2fd;
}

.model-performance-card.claude {
  border-color: #7b1fa2;
  background: #f3e5f5;
}

.model-performance-card.grok {
  border-color: #f57c00;
  background: #fff3e0;
}

.model-name {
  font-weight: 600;
  margin-bottom: 0.5rem;
  text-align: center;
}

.performance-metrics {
  display: flex;
  justify-content: space-around;
}

.insights-section {
  margin-top: 2rem;
}

.insights-grid {
  display: grid;
  gap: 1rem;
}

.insight-card {
  padding: 1.5rem;
  border-radius: 8px;
  border-left: 4px solid #667eea;
  background: #f8f9fa;
}

.insight-card.cost_savings {
  border-left-color: #28a745;
}

.insight-card.accuracy_concern {
  border-left-color: #dc3545;
}

.insight-card.model_recommendation {
  border-left-color: #17a2b8;
}

.insight-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
}

.insight-header h5 {
  margin: 0;
  color: #2c3e50;
}

.confidence-badge {
  background: #e9ecef;
  color: #495057;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.insight-description {
  color: #495057;
  margin-bottom: 0.75rem;
  white-space: pre-line;
}

.insight-action, .insight-savings {
  font-size: 0.875rem;
  margin-top: 0.5rem;
}

.insight-savings {
  color: #28a745;
  font-weight: 600;
}

.container {
  max-width: none;
  margin: 0;
  padding: 0;
}

/* Accuracy Analysis Styles */
.accuracy-analysis-section {
  margin-top: 1.5rem;
  padding: 1.25rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  color: #0f172a;
}

.accuracy-analysis-section h4 {
  color: #0f172a;
  margin-bottom: 1.25rem;
}

.accuracy-overview {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.accuracy-metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.metric-card {
  background: #fff;
  padding: 1.25rem;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.metric-card h5 {
  color: #0f172a;
  margin-bottom: 1rem;
  font-size: 1.05rem;
}

.recommended-model {
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--p-accent, #1e3a5f);
}

.validation-progress {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #28a745, #20c997);
  transition: width 0.3s ease;
}

.model-accuracy-comparison {
  background: #fff;
  padding: 1.25rem;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.model-accuracy-comparison h5 {
  color: #0f172a;
  margin-bottom: 1rem;
}

.accuracy-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
}

.model-accuracy-card {
  background: #f8fafc;
  border-radius: 8px;
  padding: 1.25rem;
  border: 1px solid #e2e8f0;
}

.model-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.model-header h6 {
  color: #0f172a;
  margin: 0;
  font-size: 1.05rem;
}

.accuracy-score {
  font-size: 1.2rem;
  font-weight: bold;
  color: var(--p-accent, #1e3a5f);
}

.accuracy-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.accuracy-metric {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.metric-label {
  color: #64748b;
  font-size: 0.9rem;
}

.metric-value {
  color: #0f172a;
  font-weight: 600;
}

/* Enhanced Results Section Styles */
.enhanced-results-section {
  margin-top: 2rem;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.results-filters {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.filter-btn {
  padding: 0.5rem 1rem;
  border: 2px solid #dee2e6;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  font-weight: 500;
}

.filter-btn.active {
  background: #667eea;
  color: white;
  border-color: #667eea;
}

.filter-btn.disagreement.active {
  background: #dc3545;
  border-color: #dc3545;
}

.filter-btn.validation.active {
  background: #ffc107;
  border-color: #ffc107;
  color: #000;
}

.enhanced-results-grid {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.enhanced-result-card {
  border: 1px solid #dee2e6;
  border-radius: 12px;
  background: white;
  padding: 1.5rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.enhanced-result-card:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  transform: translateY(-2px);
}

.enhanced-result-card.models-disagree {
  border-left: 4px solid #dc3545;
  background: linear-gradient(90deg, #fff5f5 0%, white 10%);
}

.enhanced-result-card.needs-validation {
  border-left: 4px solid #ffc107;
  background: linear-gradient(90deg, #fffbf0 0%, white 10%);
}

.enhanced-result-card.validated {
  border-left: 4px solid #28a745;
  background: linear-gradient(90deg, #f8fff9 0%, white 10%);
}

.result-card-header {
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f1f3f4;
}

.result-title {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
}

.result-title h6 {
  color: #2c3e50;
  font-size: 1.1rem;
  margin: 0;
  font-weight: 600;
}

.requirement-identifier {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-top: 0.5rem;
}

.req-code {
  background: #e3f2fd;
  color: #1565c0;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.85rem;
  font-weight: 600;
}

.req-name {
  color: #666;
  font-size: 0.9rem;
  font-style: italic;
}

.status-indicators {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.status-badge.disagreement {
  background: #dc3545;
  color: white;
}

.status-badge.validated {
  background: #28a745;
  color: white;
}

.status-badge.needs-validation {
  background: #ffc107;
  color: #000;
}

.document-name {
  color: #6c757d;
  font-style: italic;
}

.requirement-summary {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 3px solid #667eea;
}

.requirement-text strong {
  color: #495057;
  display: block;
  margin-bottom: 0.5rem;
}

.requirement-text p {
  margin: 0;
  color: #6c757d;
  line-height: 1.5;
}

.model-results-comparison {
  margin-bottom: 1.5rem;
}

.comparison-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e9ecef;
}

.comparison-header h6 {
  margin: 0;
  color: #495057;
  font-size: 1rem;
}

.agreement-indicator {
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.agreement-indicator.agree {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.agreement-indicator.disagree {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.models-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1rem;
}

.model-result-card {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem;
  background: white;
  transition: all 0.2s ease;
}

.model-result-card:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.model-result-card.result-pass {
  border-left: 4px solid #28a745;
  background: linear-gradient(90deg, #f8fff9 0%, white 15%);
}

.model-result-card.result-fail {
  border-left: 4px solid #dc3545;
  background: linear-gradient(90deg, #fff5f5 0%, white 15%);
}

.model-result-card.result-unclear {
  border-left: 4px solid #ffc107;
  background: linear-gradient(90deg, #fffbf0 0%, white 15%);
}

.model-result-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f1f3f4;
}

.model-conclusion {
  margin-bottom: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.conclusion-header {
  margin-bottom: 0.75rem;
  color: #495057;
  font-size: 0.95rem;
}

.conclusion-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.conclusion-content.conclusion-pass {
  border-left: 3px solid #28a745;
  padding-left: 0.75rem;
}

.conclusion-content.conclusion-fail {
  border-left: 3px solid #dc3545;
  padding-left: 0.75rem;
}

.conclusion-content.conclusion-unclear {
  border-left: 3px solid #ffc107;
  padding-left: 0.75rem;
}

.conclusion-result {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.result-label {
  color: #6c757d;
  font-weight: 500;
  min-width: 60px;
}

.result-value {
  font-weight: 600;
  font-size: 1rem;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
}

.result-value.result-pass {
  background: #d4edda;
  color: #155724;
}

.result-value.result-fail {
  background: #f8d7da;
  color: #721c24;
}

.result-value.result-unclear {
  background: #fff3cd;
  color: #856404;
}

.conclusion-reasoning {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.reasoning-label {
  color: #495057;
  font-weight: 500;
  font-size: 0.9rem;
}

.reasoning-summary-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.reasoning-summary {
  margin: 0;
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.4;
  background: white;
  padding: 0.75rem;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
}

.reasoning-summary.expanded {
  max-height: none;
  background: #f8f9fa;
  border-color: #667eea;
}

.expand-summary-btn {
  align-self: flex-start;
  background: none;
  border: none;
  color: #667eea;
  font-size: 0.8rem;
  cursor: pointer;
  padding: 0.25rem 0;
  text-decoration: underline;
  transition: color 0.2s ease;
}

.expand-summary-btn:hover {
  color: #5a6fd8;
}

.conclusion-evidence {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.evidence-label {
  color: #495057;
  font-weight: 500;
  font-size: 0.9rem;
}

.evidence-text {
  margin: 0;
  color: #495057;
  font-size: 0.9rem;
  line-height: 1.4;
  background: #e3f2fd;
  padding: 0.75rem;
  border-radius: 6px;
  border-left: 3px solid #2196f3;
  font-style: italic;
}

.debug-results {
  display: block;
  margin-top: 0.25rem;
  font-family: monospace;
  color: #6c757d;
  font-size: 0.75rem;
}

.model-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.model-name {
  font-weight: 600;
  color: #2c3e50;
}

.model-icon {
  font-size: 1.2rem;
}

.result-decision {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;
}

.result-decision.decision-pass {
  background: #d4edda;
  color: #155724;
}

.result-decision.decision-fail {
  background: #f8d7da;
  color: #721c24;
}

.result-decision.decision-unclear {
  background: #fff3cd;
  color: #856404;
}

.model-metrics {
  margin-bottom: 1rem;
}

.metric-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.metric-label {
  color: #6c757d;
  font-size: 0.9rem;
}

.metric-value {
  font-weight: 500;
  color: #495057;
}

.confidence-bar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 120px;
}

.confidence-bar .confidence-fill {
  height: 6px;
  border-radius: 3px;
  transition: all 0.3s ease;
  flex: 1;
}

.confidence-fill.confidence-high {
  background: linear-gradient(90deg, #28a745, #20c997);
}

.confidence-fill.confidence-medium {
  background: linear-gradient(90deg, #ffc107, #fd7e14);
}

.confidence-fill.confidence-low {
  background: linear-gradient(90deg, #dc3545, #e74c3c);
}

.confidence-text {
  font-size: 0.8rem;
  font-weight: 600;
  color: #495057;
  min-width: 40px;
}

.reasoning-preview {
  margin-top: 1rem;
}

.reasoning-toggle {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  text-align: left;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.reasoning-toggle:hover {
  background: #e9ecef;
}

.reasoning-content {
  margin-top: 0.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 3px solid #667eea;
}

.reasoning-content p {
  margin: 0;
  color: #495057;
  line-height: 1.5;
  font-size: 0.9rem;
}

.model-validation-actions {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.model-action-btn {
  padding: 0.5rem 1rem;
  border: 2px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 500;
  transition: all 0.2s ease;
  background: #f8f9fa;
  color: #495057;
}

.model-action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.model-action-btn.validate-correct {
  border-color: #28a745;
  color: #28a745;
}

.model-action-btn.validate-correct:hover {
  background: #28a745;
  color: white;
}

.model-action-btn.validate-correct.active {
  background: #28a745;
  color: white;
  box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.25);
}

.model-action-btn.validate-wrong {
  border-color: #dc3545;
  color: #dc3545;
}

.model-action-btn.validate-wrong:hover {
  background: #dc3545;
  color: white;
}

.model-action-btn.validate-wrong.active {
  background: #dc3545;
  color: white;
  box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.25);
}

.model-action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
}

.model-action-btn.loading {
  opacity: 0.8;
}

.model-action-btn.validate-partial {
  border-color: #ffc107;
  color: #856404;
}

.model-action-btn.validate-partial:hover {
  background: #ffc107;
  color: #000;
}

.model-action-btn.validate-partial.active {
  background: #ffc107;
  color: #000;
  box-shadow: 0 0 0 3px rgba(255, 193, 7, 0.25);
}

/* Correction Modal Styles */
.correction-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.current-response-section {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  border-left: 3px solid #667eea;
}

.current-response {
  margin-top: 0.5rem;
}

.response-item {
  margin-bottom: 1rem;
}

.response-item:last-child {
  margin-bottom: 0;
}

.current-reasoning {
  background: white;
  padding: 0.75rem;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  margin: 0.5rem 0 0 0;
  color: #495057;
  font-style: italic;
}

.correction-inputs {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  color: #495057;
  font-weight: 500;
}

.form-control {
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 0.9rem;
  transition: border-color 0.2s ease;
}

.form-control:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.25);
  outline: none;
}

.form-text {
  color: #6c757d;
  font-size: 0.8rem;
  font-style: italic;
}

.issue-categories {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.checkbox-label:hover {
  background: #e9ecef;
}

.checkbox-label input[type="checkbox"] {
  margin: 0;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1rem;
  border-top: 1px solid #e9ecef;
  background: #f8f9fa;
}

/* Document Viewer Styles */
.document-viewer-section {
  margin-top: 1.5rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
}

.document-viewer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.document-toggle-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  color: #495057;
}

.document-toggle-btn:hover {
  background: #e9ecef;
  transform: translateY(-1px);
}

.document-toggle-btn.expanded {
  background: #667eea;
  color: white;
  border-color: #667eea;
}

.document-name-small {
  color: #6c757d;
  font-style: italic;
}

.document-viewer-content {
  padding: 1rem;
  background: white;
}

.document-preview {
  width: 100%;
  height: 600px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #dee2e6;
}

.pdf-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.pdf-preview {
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 8px;
}

.pdf-fallback {
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.8rem;
}

.pdf-fallback a {
  color: #ffd700;
  text-decoration: underline;
}

.no-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #6c757d;
  background: #f8f9fa;
}

.no-preview i {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #dee2e6;
}

.no-preview p {
  margin: 0.5rem 0;
  font-size: 1.1rem;
}

.no-preview small {
  color: #adb5bd;
  font-size: 0.8rem;
}

.loading-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #667eea;
  background: #f8f9fa;
}

.loading-preview i {
  font-size: 2rem;
  margin-bottom: 1rem;
}

.loading-preview p {
  margin: 0;
  font-size: 1rem;
  color: #495057;
}

.result-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #f1f3f4;
  flex-wrap: wrap;
  gap: 1rem;
}

.action-group {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.action-btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.action-btn.validate-pass {
  background: #28a745;
  color: white;
}

.action-btn.validate-pass:hover {
  background: #218838;
  transform: translateY(-1px);
}

.action-btn.validate-fail {
  background: #dc3545;
  color: white;
}

.action-btn.validate-fail:hover {
  background: #c82333;
  transform: translateY(-1px);
}

.action-btn.feedback {
  background: #667eea;
  color: white;
}

.action-btn.feedback:hover {
  background: #5a6fd8;
  transform: translateY(-1px);
}

.winner-indicator {
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
}

.winner-indicator:not(.disagreement) {
  background: #d4edda;
  color: #155724;
}

.winner-indicator.disagreement {
  background: #fff3cd;
  color: #856404;
}

.winner-text {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .accuracy-metrics {
    grid-template-columns: 1fr;
  }
  
  .accuracy-grid {
    grid-template-columns: 1fr;
  }
  
  .accuracy-overview {
    gap: 1rem;
  }
  
  .results-header {
    flex-direction: column;
    align-items: stretch;
  }
  
  .models-grid {
    grid-template-columns: 1fr;
  }
  
  .result-actions {
    flex-direction: column;
    align-items: stretch;
  }
  
  .action-group {
    justify-content: center;
  }
}

/* Enhanced Modal Header */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
}

.feedback-status {
  position: absolute;
  top: -0.5rem;
  right: 3rem;
}

.feedback-status .badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

/* Feedback Indicator */
.feedback-indicator {
  margin-top: 0.5rem;
  text-align: center;
}

.feedback-badge {
  display: inline-block;
  background: #e3f2fd;
  color: #1976d2;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #bbdefb;
}

.feedback-badge:hover {
  background: #bbdefb;
  color: #0d47a1;
  transform: translateY(-1px);
}

/* Pagination Controls */
.pagination-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding: 15px 0;
  border-top: 1px solid #dee2e6;
}

.pagination-info {
  color: #6c757d;
  font-size: 14px;
}

.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 15px;
}

.page-info {
  color: #495057;
  font-weight: 500;
  font-size: 14px;
}

.btn-outline.btn-sm {
  padding: 6px 12px;
  font-size: 13px;
  border: 1px solid #dee2e6;
  color: #495057;
  background: white;
  border-radius: 4px;
  transition: all 0.2s;
}

.btn-outline.btn-sm:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #adb5bd;
}

.btn-outline.btn-sm:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* View Toggle */
.view-toggle {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

/* By Requirement View */
.by-requirement-container {
  margin-top: 20px;
}

.requirement-selector {
  margin-bottom: 30px;
}

.requirement-selector label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #495057;
}

.requirement-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #007bff;
}

.requirement-header h4 {
  margin: 0;
  color: #212529;
  flex: 1;
}

.bulk-actions {
  display: flex;
  gap: 10px;
}

.requirement-test-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 20px;
}

.test-result-card {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 15px;
  background: white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.test-result-card .card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #dee2e6;
}

.test-result-card h6 {
  margin: 0;
  color: #495057;
  font-size: 14px;
}

.experiment-badge {
  background: #e3f2fd;
  color: #1976d2;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}

.model-results-grid {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.model-result {
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 12px;
  background: #fafafa;
}

.model-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.model-name {
  font-weight: 600;
  color: #495057;
}

.result-badge {
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}

.result-pass { background: #d4edda; color: #155724; }
.result-fail { background: #f8d7da; color: #721c24; }
.result-unclear { background: #fff3cd; color: #856404; }

.model-details {
  display: flex;
  gap: 15px;
  margin-bottom: 8px;
  font-size: 12px;
  color: #6c757d;
}

.reasoning-preview {
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 10px;
  line-height: 1.4;
}

.validation-controls {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

/* Updated validation buttons to match experiment modal */
.validation-btn {
  padding: 6px 12px;
  border: 2px solid #dee2e6;
  background: white;
  color: #6c757d;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 12px;
  font-weight: 500;
  position: relative;
}

/* Default state - no feedback provided */
.validation-btn:hover {
  border-color: #adb5bd;
  color: #495057;
}

/* Feedback provided but not selected */
.validation-btn.has-feedback:not(.selected) {
  border-color: #adb5bd;
  background: #f8f9fa;
  color: #495057;
}

/* Selected states with clear colors */
.validation-btn.correct-btn.selected {
  background: #28a745;
  border-color: #28a745;
  color: white;
  box-shadow: 0 2px 4px rgba(40, 167, 69, 0.3);
}

.validation-btn.wrong-btn.selected {
  background: #dc3545;
  border-color: #dc3545;
  color: white;
  box-shadow: 0 2px 4px rgba(220, 53, 69, 0.3);
}

.validation-btn.partial-btn.selected {
  background: #ffc107;
  border-color: #ffc107;
  color: #212529;
  box-shadow: 0 2px 4px rgba(255, 193, 7, 0.3);
}

/* Add checkmark for selected buttons */
.validation-btn.selected::after {
  content: '✓';
  position: absolute;
  top: -5px;
  right: -5px;
  background: white;
  color: inherit;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

/* Main Results Reasoning Section */
.reasoning-section {
  margin: 10px 0;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 3px solid #007bff;
}

.reasoning-preview {
  font-size: 13px;
  color: #495057;
  line-height: 1.4;
  margin-bottom: 8px;
}

.expand-reasoning-btn {
  background: none;
  border: none;
  color: #007bff;
  cursor: pointer;
  font-size: 12px;
  padding: 0;
  text-decoration: underline;
}

.expand-reasoning-btn:hover {
  color: #0056b3;
}

.full-reasoning-content {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #dee2e6;
}

.full-reasoning-content h6 {
  margin: 0 0 8px 0;
  color: #495057;
  font-size: 13px;
}

.full-reasoning-content p {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #212529;
}

/* Responsive adjustments for requirement view */
@media (max-width: 768px) {
  .requirement-test-grid {
    grid-template-columns: 1fr;
  }
  
  .requirement-header {
    flex-direction: column;
    gap: 15px;
  }
  
  .bulk-actions {
    align-self: stretch;
  }
  
  .bulk-actions .btn {
    flex: 1;
  }
}
</style>
